import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol } from '../utils/currency';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebase, getProdDb } from '@services/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { readGlobalProfiles, writeGlobalProfile, updateGlobalProfile, deleteGlobalProfile } from '@services/firebaseGlobal';
import { useAlert } from '../contexts/ToastContext';
import { readUserCollection } from '@services/firebaseUser';
import { uploadToStorage, getDownloadUrl, getPermanentUrl, getEnvStorageBucket } from '../utils/storageUtils';
import { 
  FaCalendarAlt, 
  FaChevronLeft, 
  FaChevronRight,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTimes,
  FaEdit,
  FaTrashAlt,
  FaSearchLocation,
  FaRoute,
  FaRedo,
  FaTruck,
  FaArrowUp,
  FaSave,
  FaClipboardList,
  FaShareAlt,
  FaPrint,
  FaWhatsapp,
  FaComments,
  FaUpload
} from 'react-icons/fa';
import ImageZoomModal from '@components/modals/ImageZoomModal';
import './css/Organize.css';

const { auth, db } = getFirebase();
const prodDb = getProdDb();

const GOOGLE_MAPS_API_KEY = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;

const getBackendUrl = () => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocal
    ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
    : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
};

const Organize = ({ userRole }) => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const showAlert = useAlert();
  
  const [loading, setLoading] = useState(true);
  const [loadingDistances, setLoadingDistances] = useState(false);
  const [error, setError] = useState(null);
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [adminAddress, setAdminAddress] = useState(null);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'deliveryDate', 
    direction: 'asc',
    secondaryKey: 'deliveryTime',
    secondaryDirection: 'asc'
  });
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [customerModal, setCustomerModal] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [priceLoading, setPriceLoading] = useState(false);
  const [editedAddress, setEditedAddress] = useState('');
  const [addressEditing, setAddressEditing] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  
  // Optimization modal states
  const [optimizeModalOpen, setOptimizeModalOpen] = useState(false);
  const [optimizationRoute, setOptimizationRoute] = useState([]);
  const [optimizationLoading, setOptimizationLoading] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState(null);

  // Saved Analysis states
  const [savedAnalyses, setSavedAnalyses] = useState(() => {
    try { return JSON.parse(localStorage.getItem('organize_saved_analyses') || '[]'); }
    catch { return []; }
  });
  const [activeAnalysis, setActiveAnalysis] = useState(null);
  const [showSaveAnalysisModal, setShowSaveAnalysisModal] = useState(false);
  const [saveAnalysisRows, setSaveAnalysisRows] = useState([]);
  const [saveAnalysisStartTime, setSaveAnalysisStartTime] = useState('');
  const [saveAnalysisName, setSaveAnalysisName] = useState('');
  const [saveAnalysisReturnMins, setSaveAnalysisReturnMins] = useState(0);
  const [saveAnalysisReturnDistance, setSaveAnalysisReturnDistance] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [editingAnalysisId, setEditingAnalysisId] = useState(null); // null = new, string id = editing
  const [dbPlans, setDbPlans] = useState([]);
  const [publishingPlan, setPublishingPlan] = useState(false);
  const [refreshingPlans, setRefreshingPlans] = useState(false);
  
  // Refs for caching
  const distanceCache = useRef({});
  const postalCodeCache = useRef({});
  const googleMapsLoaded = useRef(false);
  const calculationInProgress = useRef(false);
  const mapContainerRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const infoWindowsRef = useRef([]);

  // Add these map-related states
  const [mapInstance, setMapInstance] = useState(null);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(null);
  const currentCalculationId = useRef(null);
  const timerRef = useRef(null);

  // Test address location autocomplete
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationDebounceRef = useRef(null);
  const streetDebounceRef = useRef(null);
  const geocodeCacheRef = useRef({});
  // Full German locality dataset cached after first load (~8200 entries)
  const allLocalitiesRef = useRef(null);
  const localitiesLoadingRef = useRef(false);
  // Street autocomplete
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [streetLoading, setStreetLoading] = useState(false);
  // Cache of all streets per postcode: { '65199': [{name,postalCode,locality},...] }
  const streetCacheRef = useRef({});
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [pendingCalcSignal, setPendingCalcSignal] = useState(0);
  const filteredOrdersRef = useRef(filteredOrders);
  const abortCalculation = useRef(false);
  const mapInitId = useRef(null);
  const geocodeCache = useRef({});
  const polylineRef = useRef(null);
  const shareBtnRef = useRef(null);
  const shareMenuRef = useRef(null);
  // Map of userId -> { name, phone, email } for resolving app order user details
  const userProfileMapRef = useRef({});

  // Close share menu when clicking outside
  useEffect(() => {
    if (!showShareMenu) return;
    const handler = (e) => {
      if (
        shareMenuRef.current && !shareMenuRef.current.contains(e.target) &&
        shareBtnRef.current && !shareBtnRef.current.contains(e.target)
      ) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showShareMenu]);

  // Load user profile map so app-order userId can be resolved to name/phone/email
  useEffect(() => {
    if (!user) return;
    const loadProfiles = async () => {
      try {
        const [profileSnap, userList] = await Promise.all([
          getDocs(collection(prodDb, 'profile')),
          readGlobalProfiles('cUsers'),
        ]);
        const map = {};
        userList.forEach((u) => {
          map[u.id] = { email: u.email || '' };
        });
        profileSnap.docs.forEach((d) => {
          const pd = d.data();
          map[d.id] = {
            ...(map[d.id] || {}),
            name: [pd.f_name, pd.l_name].filter(Boolean).join(' ') || '',
            email: pd.email || map[d.id]?.email || '',
            phone: pd.phone || '',
          };
        });
        userProfileMapRef.current = map;
      } catch (e) {
        console.error('Organize: error loading user profile map:', e);
      }
    };
    loadProfiles();
  }, [user]);

  // Sorted merged list of local + published plans
  const sortPlansList = (plans) => plans.slice().sort((a, b) => {
    const tA = a.startTime || ''; const tB = b.startTime || '';
    if (tA !== tB) return tA < tB ? -1 : 1;
    const seqA = parseInt((a.name.match(/^Plan(\d+)_/) || [])[1] || 0);
    const seqB = parseInt((b.name.match(/^Plan(\d+)_/) || [])[1] || 0);
    if (seqA !== seqB) return seqA - seqB;
    return (a.date || '') < (b.date || '') ? -1 : 1;
  });
  const allPlans = sortPlansList([
    ...savedAnalyses.map(p => ({ ...p, source: 'local' })),
    ...dbPlans,
  ]);

  // Restrict access to Movers and Admins only
  useEffect(() => {
    if (userRole == 'User' || userRole == 'Buyer') {
      navigate('/login', { replace: true });
    }
  }, [userRole, navigate]);

  // For Mover: auto-open plan view (first sorted plan, including db plans) on mount
  useEffect(() => {
    if (userRole === 'Mover' && allPlans.length > 0 && !activeAnalysis) {
      setActiveAnalysis(allPlans[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, dbPlans]);

  // Load published plans from Firestore — also called by the refresh button
  const handleRefreshPlans = async () => {
    if (!user || refreshingPlans) return;
    setRefreshingPlans(true);
    try {
      const plans = await readGlobalProfiles('deliveryDetails');
      const mapped = plans.map(p => ({ ...p, source: 'db' }));
      setDbPlans(mapped);
      // If the active plan is a db plan, refresh it with the latest data
      setActiveAnalysis(prev => {
        if (!prev || prev.source !== 'db') return prev;
        const fresh = mapped.find(p => p.id === prev.id);
        return fresh || prev;
      });
    } catch (e) {
      console.error('Error loading delivery plans:', e);
    } finally {
      setRefreshingPlans(false);
    }
  };

  useEffect(() => {
    handleRefreshPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (userRole == 'User' || userRole == 'Buyer') {
    return null;
  }

  // Handle checkbox selection
  const handleCheckboxChange = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  // Select all checkboxes
  const handleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      const allIds = new Set(filteredOrders.map(order => order.id));
      setSelectedOrders(allIds);
    }
  };

  // Calculate optimized route
  const calculateOptimizedRoute = async () => {
    setOptimizationLoading(true);
    setOptimizationResult(null);

    try {
      // Get selected orders in their current order
      const selectedOrdersArray = filteredOrders.filter(order => 
        selectedOrders.has(order.id)
      );

      // Create initial route: Home -> selected orders -> Home
      const route = [
        { 
          id: 'admin-start',
          type: 'admin',
          address: adminAddress,
          name: 'Home', // Changed from 'Admin (Start)' to 'Home'
          order: null
        },
        ...selectedOrdersArray.map(order => ({
          id: order.id,
          type: 'customer',
          address: order.correctedAddress || order.address,
          name: order.name,
          order: order,
          originalIndex: selectedOrdersArray.indexOf(order)
        })),
        { 
          id: 'admin-end',
          type: 'admin',
          address: adminAddress,
          name: 'Admin (End)', // We'll hide this row in display
          order: null
        }
      ];

      setOptimizationRoute(route);
      setOptimizeModalOpen(true);

      // Calculate distances between consecutive points
      const routeWithDistances = [...route];
      
      for (let i = 0; i < route.length - 1; i++) {
        const fromAddress = route[i].address;
        const toAddress = route[i + 1].address;
        
        try {
          const [formattedOrigin, formattedDestination] = await Promise.all([
            formatAddressForDistance(fromAddress),
            formatAddressForDistance(toAddress)
          ]);
          
          if (formattedOrigin && formattedDestination) {
            const cacheKey = `${formattedOrigin}-${formattedDestination}`;
            
            if (distanceCache.current[cacheKey]) {
              routeWithDistances[i].toNext = {
                distance: distanceCache.current[cacheKey].distance,
                duration: distanceCache.current[cacheKey].duration,
                distanceValue: distanceCache.current[cacheKey].distanceValue
              };
            } else {
              const result = await calculateDistanceWithGoogle(formattedOrigin, formattedDestination);
              routeWithDistances[i].toNext = {
                distance: result.distance,
                duration: result.duration,
                distanceValue: result.distanceValue
              };
              
              // Cache the result
              distanceCache.current[cacheKey] = result;
            }
          } else {
            routeWithDistances[i].toNext = {
              distance: 'N/A',
              duration: 'N/A',
              distanceValue: null
            };
          }
        } catch (error) {
          console.error(`Error calculating distance from ${i} to ${i + 1}:`, error);
          routeWithDistances[i].toNext = {
            distance: 'Error',
            duration: 'Error',
            distanceValue: null
          };
        }
      }

      // Calculate totals
      const totalDistance = routeWithDistances.reduce((sum, point, index) => {
        if (index < routeWithDistances.length - 1 && point.toNext && point.toNext.distanceValue) {
          return sum + point.toNext.distanceValue;
        }
        return sum;
      }, 0);

      const totalDuration = routeWithDistances.reduce((sum, point, index) => {
        if (index < routeWithDistances.length - 1 && point.toNext && point.toNext.duration) {
          // Parse duration string (e.g., "1 hour 30 mins" or "2 hours 15 mins")
          const durationStr = point.toNext.duration;
          let minutes = 0;
          
          // Extract hours and minutes
          const hoursMatch = durationStr.match(/(\d+)\s*hour/);
          const minsMatch = durationStr.match(/(\d+)\s*min/);
          
          if (hoursMatch) minutes += parseInt(hoursMatch[1]) * 60;
          if (minsMatch) minutes += parseInt(minsMatch[1]);
          
          return sum + minutes;
        }
        return sum;
      }, 0);

      setOptimizationResult({
        totalDistance: (totalDistance / 1000).toFixed(2) + ' km',
        totalDuration: formatDuration(totalDuration),
        route: routeWithDistances
      });

    } catch (error) {
      console.error('Error calculating optimized route:', error);
      showAlert('Failed to calculate optimized route. Please try again.');
    } finally {
      setOptimizationLoading(false);
    }
  };

  // Format duration in minutes to readable format
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0 && mins > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${mins} min${mins > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${mins} min${mins > 1 ? 's' : ''}`;
    }
  };

  // ── Saved Analysis helpers ─────────────────────────────────────────────────
  const parseDurationMins = (durationStr) => {
    if (!durationStr) return 0;
    let mins = 0;
    const hr = durationStr.match(/(\d+)\s*hour/);
    const mn = durationStr.match(/(\d+)\s*min/);
    if (hr) mins += parseInt(hr[1]) * 60;
    if (mn) mins += parseInt(mn[1]);
    return mins;
  };

  const addMinsToTime = (timeStr, minutes) => {
    const [h, m] = timeStr.split(':').map(Number);
    // Use modular arithmetic to handle negative minutes (back-calculation)
    let total = ((h * 60 + m + minutes) % 1440 + 1440) % 1440;
    const nh = Math.floor(total / 60);
    const nm = total % 60;
    return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
  };

  const generateAnalysisName = (dateStr, startTime) => {
    // Convert YYYY-MM-DD → DDMMYY
    const toDDMMYY = (d) => {
      if (!d) return '';
      const parts = d.split('-');
      if (parts.length === 3) return parts[2] + parts[1] + parts[0].slice(-2);
      return d;
    };
    const ddmmyy = toDDMMYY(dateStr);
    const hhmm = startTime ? startTime.replace(':', '') : '';
    // Seq is per (date + startTime) — resets to 1 for a different start time
    const existing = savedAnalyses.filter(a => a.date === dateStr && (a.startTime || '') === (startTime || ''));
    let maxSeq = 0;
    existing.forEach(a => {
      const match = a.name.match(/^Plan(\d+)_/);
      if (match) maxSeq = Math.max(maxSeq, parseInt(match[1], 10));
    });
    const seqNo = maxSeq + 1;
    return `Plan${seqNo}_${ddmmyy}${hhmm ? '_' + hhmm : ''}`;
  };

  const openSaveAnalysisModal = () => {
    if (!optimizationResult) return;
    const route = optimizationResult.route;
    const adminStart = route.find(p => p.id === 'admin-start');
    const customerPoints = route.filter(p => p.type === 'customer');
    const rows = customerPoints.map((p, i) => {
      const prevPoint = i === 0 ? adminStart : customerPoints[i - 1];
      const order = p.order || {};
      const rawData = order.rawData || {};
      const email = rawData['E-Mail-Address'] || '';
      const deliveryDate = order.deliveryDate || '';
      // Determine source collection: FormOrders vs app order collection
      const srcCollection = rawData['E-Mail-Address'] ? 'FormOrders' : 'order';
      // orderRef: for FormOrders use email|date; for app orders use the doc ID itself
      const orderRef = srcCollection === 'FormOrders'
        ? (email && deliveryDate ? `${email}|${deliveryDate}` : '')
        : (p.id || '');
      return {
        id: p.id,
        name: p.name,
        date: deliveryDate,
        requestedTime: order.deliveryTime || '',
        expectedTime: '',
        incomingDistance: prevPoint?.toNext?.distance || '-',
        incomingDurationMins: parseDurationMins(prevPoint?.toNext?.duration || ''),
        collection: srcCollection,
        orderRef,
      };
    });
    const defaultDate = rows[0]?.date || '';
    // Return-to-home leg is stored on the last customer point's toNext
    const lastCust = customerPoints[customerPoints.length - 1];
    const returnMins = parseDurationMins(lastCust?.toNext?.duration || '');
    const returnDist = lastCust?.toNext?.distance || '';
    setSaveAnalysisRows(rows);
    setSaveAnalysisName(generateAnalysisName(defaultDate, ''));
    setSaveAnalysisStartTime('');
    setSaveAnalysisReturnMins(returnMins);
    setSaveAnalysisReturnDistance(returnDist);
    setShowSaveAnalysisModal(true);
  };

  // Open modal to EDIT an existing saved analysis (populate from saved entry)
  const openEditAnalysisModal = (analysis) => {
    // Reconstruct per-leg durations from stored expected times vs start time
    // so that recalcExpectedTimes works correctly when start time is changed
    const parseTimeToMins = (t) => {
      if (!t) return null;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const startMins = parseTimeToMins(analysis.startTime);
    let prevCumMins = 0;
    const rows = analysis.rows.map(r => {
      const expMins = parseTimeToMins(r.expectedTime);
      let incomingDurationMins = 0;
      if (startMins !== null && expMins !== null) {
        const cumMins = ((expMins - startMins) + 1440) % 1440;
        incomingDurationMins = cumMins - prevCumMins;
        prevCumMins = cumMins;
      }
      return {
        id: r.name,
        name: r.name,
        date: r.date,
        requestedTime: r.requestedTime,
        expectedTime: r.expectedTime,
        incomingDistance: r.distance,
        incomingDurationMins,
        collection: r.collection || 'FormOrders',
        orderRef: r.orderRef || '',
      };
    });
    setSaveAnalysisRows(rows);
    setSaveAnalysisName(analysis.name);
    setSaveAnalysisStartTime(analysis.startTime || '');
    setSaveAnalysisReturnMins(analysis.returnMins ?? 0);
    setSaveAnalysisReturnDistance(analysis.returnDistance || '');
    setEditingAnalysisId(analysis.id);
    setShowSaveAnalysisModal(true);
  };

  const recalcExpectedTimes = (rows, startTime) => {
    if (!startTime) return rows.map(r => ({ ...r, expectedTime: '' }));
    let cumMins = 0;
    return rows.map(r => {
      cumMins += r.incomingDurationMins;
      return { ...r, expectedTime: addMinsToTime(startTime, cumMins) };
    });
  };

  const handleSaveAnalysis = async () => {
    const dateStr = saveAnalysisRows[0]?.date || '';
    const lastRow = saveAnalysisRows[saveAnalysisRows.length - 1];
    // Store endTime whenever expected times are set (returnMins may be 0 if distance not loaded)
    const endTime = lastRow?.expectedTime
      ? addMinsToTime(lastRow.expectedTime, saveAnalysisReturnMins)
      : '';

    const buildRows = (rows) => rows.map(r => ({
      name: r.name,
      date: r.date,
      requestedTime: r.requestedTime,
      expectedTime: r.expectedTime,
      distance: r.incomingDistance,
      collection: r.collection || 'FormOrders',
      orderRef: r.orderRef || '',
    }));

    if (editingAnalysisId) {
      const isDbPlan = dbPlans.some(p => p.id === editingAnalysisId);

      if (isDbPlan) {
        // Update the Firestore document
        const existingDb = dbPlans.find(p => p.id === editingAnalysisId);
        const updatedData = {
          name: saveAnalysisName.trim() || existingDb?.name || '',
          startTime: saveAnalysisStartTime,
          endTime,
          returnMins: saveAnalysisReturnMins,
          returnDistance: saveAnalysisReturnDistance || existingDb?.returnDistance || '',
          rows: buildRows(saveAnalysisRows),
        };
        try {
          await updateGlobalProfile('deliveryDetails', editingAnalysisId, updatedData);
          const refreshed = dbPlans.map(p =>
            p.id === editingAnalysisId ? { ...p, ...updatedData } : p
          );
          setDbPlans(refreshed);
          if (activeAnalysis?.id === editingAnalysisId) {
            setActiveAnalysis({ ...activeAnalysis, ...updatedData });
          }
        } catch (e) {
          console.error('Error updating db plan:', e);
          showAlert('Failed to update plan in database.');
        }
        setEditingAnalysisId(null);
        setShowSaveAnalysisModal(false);
        return;
      }

      // Update the existing local entry in-place, keep its name unless user changed it
      const updated = savedAnalyses.map(a => {
        if (a.id !== editingAnalysisId) return a;
        return {
          ...a,
          name: saveAnalysisName.trim() || a.name,
          startTime: saveAnalysisStartTime,
          endTime,
          returnMins: saveAnalysisReturnMins,
          returnDistance: saveAnalysisReturnDistance || a.returnDistance,
          rows: buildRows(saveAnalysisRows),
        };
      });
      setSavedAnalyses(updated);
      localStorage.setItem('organize_saved_analyses', JSON.stringify(updated));
      // Refresh activeAnalysis if it was the one being edited
      if (activeAnalysis?.id === editingAnalysisId) {
        const found = updated.find(a => a.id === editingAnalysisId);
        setActiveAnalysis(found ? { ...found, source: 'local' } : null);
      }
      setEditingAnalysisId(null);
      setShowSaveAnalysisModal(false);
      return;
    }

    // New local entry
    const finalName = saveAnalysisName.trim() || generateAnalysisName(dateStr, saveAnalysisStartTime);
    const entry = {
      id: Date.now().toString(),
      name: finalName,
      date: dateStr,
      savedAt: Date.now(),
      summary: optimizationResult ? {
        totalDistance: optimizationResult.totalDistance,
        totalDuration: optimizationResult.totalDuration,
      } : null,
      startTime: saveAnalysisStartTime,
      endTime,
      returnMins: saveAnalysisReturnMins,
      returnDistance: saveAnalysisReturnDistance,
      rows: buildRows(saveAnalysisRows),
    };
    const updated = [...savedAnalyses, entry];
    setSavedAnalyses(updated);
    localStorage.setItem('organize_saved_analyses', JSON.stringify(updated));
    setShowSaveAnalysisModal(false);
  };

  const handleDeleteAnalysis = (id) => {
    const isDbPlan = dbPlans.some(p => p.id === id);
    if (isDbPlan) {
      deleteGlobalProfile('deliveryDetails', id).then(() => {
        const updatedDb = dbPlans.filter(p => p.id !== id);
        setDbPlans(updatedDb);
        if (activeAnalysis?.id === id) {
          const remaining = sortPlansList([
            ...savedAnalyses.map(p => ({ ...p, source: 'local' })),
            ...updatedDb,
          ]);
          setActiveAnalysis(remaining.length > 0 ? remaining[0] : null);
        }
      }).catch(e => {
        console.error('Error deleting db plan:', e);
        showAlert('Failed to delete plan from database.');
      });
      return;
    }
    const updated = savedAnalyses.filter(a => a.id !== id);
    setSavedAnalyses(updated);
    localStorage.setItem('organize_saved_analyses', JSON.stringify(updated));
    if (activeAnalysis?.id === id) {
      const remaining = sortPlansList([
        ...updated.map(p => ({ ...p, source: 'local' })),
        ...dbPlans,
      ]);
      const currentIdx = allPlans.findIndex(a => a.id === id);
      const next = remaining[currentIdx] || remaining[currentIdx - 1] || remaining[0];
      setActiveAnalysis(next || null);
    }
  };

  // Publish a local plan to Firestore and remove it from localStorage
  const handlePublishPlan = async () => {
    if (!activeAnalysis || activeAnalysis.source === 'db') return;
    setPublishingPlan(true);
    try {
      const planData = {
        name: activeAnalysis.name,
        date: activeAnalysis.date,
        startTime: activeAnalysis.startTime,
        endTime: activeAnalysis.endTime,
        returnMins: activeAnalysis.returnMins,
        returnDistance: activeAnalysis.returnDistance,
        summary: activeAnalysis.summary || null,
        rows: (activeAnalysis.rows || []).map(r => ({
          collection: r.collection || 'FormOrders',
          orderRef: r.orderRef || '',
          name: r.name,
          date: r.date,
          requestedTime: r.requestedTime,
          expectedTime: r.expectedTime,
          distance: r.distance,
        })),
      };
      const newDocId = await writeGlobalProfile('deliveryDetails', null, {
        ...planData,
        publishedBy: user?.uid || '',
      });
      const newDbPlan = { id: newDocId, ...planData, source: 'db' };
      // Remove from local storage
      const updatedLocal = savedAnalyses.filter(a => a.id !== activeAnalysis.id);
      setSavedAnalyses(updatedLocal);
      localStorage.setItem('organize_saved_analyses', JSON.stringify(updatedLocal));
      // Add to dbPlans and switch active plan
      const updatedDb = [...dbPlans, newDbPlan];
      setDbPlans(updatedDb);
      setActiveAnalysis(newDbPlan);
      showAlert('Plan published successfully.');
    } catch (e) {
      console.error('Error publishing plan:', e);
      showAlert('Failed to publish plan. Please try again.');
    } finally {
      setPublishingPlan(false);
    }
  };

  // Build canvas image of the plan and share to in-app chat
  const handleChatShare = async () => {
    setShowShareMenu(false);
    const rows = (() => {
      if (activeAnalysis) return activeAnalysis.rows;
      if (optimizationResult) return optimizationResult.route.filter(p => p.type === 'customer').map(p => ({
        name: p.name, requestedTime: p.order?.deliveryTime || '', expectedTime: '', distance: p.toNext?.distance || ''
      }));
      return [];
    })();
    if (rows.length === 0) { showAlert('Nothing to share'); return; }
    const title = activeAnalysis ? activeAnalysis.name : 'Delivery Plan';
    const summaryParts = [];
    if (activeAnalysis?.summary) {
      summaryParts.push(`Dist: ${activeAnalysis.summary.totalDistance}`, `Time: ${activeAnalysis.summary.totalDuration}`);
      if (activeAnalysis.startTime) summaryParts.push(`Start: ${activeAnalysis.startTime}`);
      if (activeAnalysis.endTime) summaryParts.push(`End: ${activeAnalysis.endTime}`);
    } else if (optimizationResult) {
      summaryParts.push(`Dist: ${optimizationResult.totalDistance}`, `Time: ${optimizationResult.totalDuration}`);
    }
    // Canvas layout
    const dpr = 2;
    const paddingX = 16;
    const colWidths = [180, 90, 90, 80];
    const totalW = colWidths.reduce((a, b) => a + b, 0) + paddingX * 2;
    const headerH = 40;
    const summaryH = summaryParts.length ? 28 : 0;
    const tableHeaderH = 32;
    const rowH = 30;
    const totalH = headerH + summaryH + tableHeaderH + rows.length * rowH + 16;
    const canvas = document.createElement('canvas');
    canvas.width = totalW * dpr;
    canvas.height = totalH * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalW, totalH);
    // Header bar
    ctx.fillStyle = '#fa4848';
    ctx.fillRect(0, 0, totalW, headerH);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 15px Arial';
    ctx.fillText(title, paddingX, headerH / 2 + 5);
    // Summary
    if (summaryParts.length) {
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, headerH, totalW, summaryH);
      ctx.fillStyle = '#555';
      ctx.font = '11px Arial';
      ctx.fillText(summaryParts.join('  ·  '), paddingX, headerH + summaryH / 2 + 4);
    }
    // Table header
    const tHeaderY = headerH + summaryH;
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, tHeaderY, totalW, tableHeaderH);
    ctx.fillStyle = '#333';
    ctx.font = 'bold 12px Arial';
    let xPos = paddingX;
    ['Name', 'Req. Time', 'Exp. Time', 'Distance'].forEach((col, i) => {
      ctx.fillText(col, xPos, tHeaderY + tableHeaderH / 2 + 4);
      xPos += colWidths[i];
    });
    // Table rows
    ctx.font = '12px Arial';
    rows.forEach((row, i) => {
      const yRow = tHeaderY + tableHeaderH + i * rowH;
      ctx.fillStyle = i % 2 === 0 ? '#ffffff' : '#fafafa';
      ctx.fillRect(0, yRow, totalW, rowH);
      ctx.fillStyle = '#333';
      xPos = paddingX;
      [row.name, row.requestedTime || '—', row.expectedTime || '—', row.distance || '—'].forEach((v, j) => {
        let text = String(v);
        const maxW = colWidths[j] - 8;
        while (ctx.measureText(text).width > maxW && text.length > 1) text = text.slice(0, -1);
        ctx.fillText(text, xPos, yRow + rowH / 2 + 4);
        xPos += colWidths[j];
      });
      ctx.strokeStyle = '#eeeeee';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, yRow + rowH);
      ctx.lineTo(totalW, yRow + rowH);
      ctx.stroke();
    });
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const blob = await fetch(dataUrl).then(r => r.blob());
      const uid = user?.uid || 'unknown';
      const chatFileName = `studio_chat/${uid}/${Date.now()}.jpg`;
      const chatFile = new File([blob], 'plan-chat.jpg', { type: 'image/jpeg' });
      await uploadToStorage(chatFileName, chatFile, 'image/jpeg', getEnvStorageBucket());
      const downloadUrl = await getPermanentUrl(chatFileName, getEnvStorageBucket());   // stored in the message
      sessionStorage.setItem('chatImageData', JSON.stringify({
        url: downloadUrl, fileName: 'plan-chat.jpg',
        timestamp: new Date().toISOString(), source: 'Delivery Plan', type: 'single'
      }));
      navigate(userRole === 'Super' || userRole === 'Admin' ? '/admin-chat' : '/user-chat');
    } catch (err) {
      console.error('Chat share failed:', err);
      showAlert('Failed to share to chat');
    }
  };

  // Build pipe-separated table text (WhatsApp / chat friendly)
  const buildShareText = () => {
    const sep = ' | ';
    const header = ['Name', 'Date', 'Req. Time', 'Exp. Time', 'Distance'].join(sep);
    const divider = ['----', '----', '---------', '---------', '--------'].join(sep);

    if (activeAnalysis) {
      const title = `*${activeAnalysis.name}*`;
      const summaryParts = [];
      if (activeAnalysis.summary) {
        summaryParts.push(`Dist: ${activeAnalysis.summary.totalDistance}`);
        summaryParts.push(`Time: ${activeAnalysis.summary.totalDuration}`);
      }
      if (activeAnalysis.startTime) summaryParts.push(`Start: ${activeAnalysis.startTime}`);
      if (activeAnalysis.endTime) summaryParts.push(`End: ${activeAnalysis.endTime}`);
      const summary = summaryParts.join(' · ');
      const rows = activeAnalysis.rows
        .map(r => [r.name, r.date, r.requestedTime || '', r.expectedTime || '', r.distance || ''].join(sep))
        .join('\n');
      return [title, summary, '', header, divider, rows].filter(Boolean).join('\n');
    }
    if (optimizationResult) {
      const title = `*Delivery Plan*`;
      const summary = `Dist: ${optimizationResult.totalDistance} · Time: ${optimizationResult.totalDuration}`;
      const rows = optimizationResult.route
        .filter(p => p.type === 'customer')
        .map(p => [
          p.name,
          p.order?.deliveryDate || '',
          p.order?.deliveryTime || '',
          '',
          p.toNext?.distance || ''
        ].join(sep))
        .join('\n');
      return [title, summary, '', header, divider, rows].filter(Boolean).join('\n');
    }
    return '';
  };

  // Open a print-only popup with just the table (not the whole app screen)
  const handlePrint = () => {
    const tableRows = (() => {
      if (activeAnalysis) return activeAnalysis.rows;
      if (optimizationResult) {
        return optimizationResult.route
          .filter(p => p.type === 'customer')
          .map(p => ({
            name: p.name,
            date: p.order?.deliveryDate || '',
            requestedTime: p.order?.deliveryTime || '',
            expectedTime: '',
            distance: p.toNext?.distance || ''
          }));
      }
      return [];
    })();
    if (tableRows.length === 0) return;
    const title = activeAnalysis ? activeAnalysis.name : 'Delivery Plan';
    const summaryLine = activeAnalysis?.summary
      ? `<p style="margin:0 0 12px">Dist: ${activeAnalysis.summary.totalDistance} &bull; Time: ${activeAnalysis.summary.totalDuration}${
          activeAnalysis.startTime ? ` &bull; Start: ${activeAnalysis.startTime}` : ''
        }${
          activeAnalysis.endTime ? ` &bull; End: ${activeAnalysis.endTime}` : ''
        }</p>`
      : (optimizationResult
          ? `<p style="margin:0 0 12px">Dist: ${optimizationResult.totalDistance} &bull; Time: ${optimizationResult.totalDuration}</p>`
          : '');
    const bodyRows = tableRows
      .map(r => `<tr><td>${r.name||''}</td><td>${r.date||''}</td><td>${r.requestedTime||''}</td><td>${r.expectedTime||''}</td><td>${r.distance||''}</td></tr>`)
      .join('');
    const html = `<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:Arial,sans-serif;padding:24px;color:#333}
h2{margin:0 0 8px}table{border-collapse:collapse;width:100%}
th,td{border:1px solid #ccc;padding:8px 12px;text-align:left}
th{background:#f0f0f0;font-weight:600}tr:nth-child(even){background:#fafafa}
</style></head><body>
<h2>${title}</h2>${summaryLine}
<table><thead><tr><th>Name</th><th>Date</th><th>Req. Time</th><th>Exp. Time</th><th>Distance</th></tr></thead>
<tbody>${bodyRows}</tbody></table></body></html>`;
    const w = window.open('', '_blank', 'width=800,height=600');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };
  // ── End Saved Analysis helpers ─────────────────────────────────────────────

  // Geocode address helper function - SIMPLIFIED
  const geocodeAddress = (geocoder, address) => {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK') {
          resolve(results);
        } else {
          console.warn(`Geocode failed for address: ${address}. Status: ${status}`);
          reject(`Geocode failed: ${status}`);
        }
      });
    });
  };

  // Initialize map function - UPDATED TO HANDLE REORDERING
  const initMap = useCallback(async (initId) => {
    if (!window.google || !window.google.maps) return;
    
    setMapLoading(true);
    const mapContainer = mapContainerRef.current;
    if (!mapContainer) {
      setMapLoading(false);
      return;
    }

    try {
      // --- Reuse existing map or create a new one ---
      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
      const darkMapStyles = [
        { elementType: 'geometry', stylers: [{ color: '#212121' }] },
        { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
        { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
        { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
        { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
        { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
        { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
        { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] },
        { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
        { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
        { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
        { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
        { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
        { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
        { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
        { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
      ];
      let map = mapInstance;
      if (!map) {
        map = new window.google.maps.Map(mapContainer, {
          zoom: 15,
          center: { lat: 52.52, lng: 13.405 },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: false,
          gestureHandling: 'cooperative',
          mapId: 'DEMO_MAP_ID',
          colorScheme: isDarkMode ? 'DARK' : 'LIGHT',
        });
        setMapInstance(map);
      } else {
        // Clear previous markers and info windows
        mapMarkers.forEach(marker => { marker.map = null; });
        infoWindowsRef.current.forEach(iw => iw.close());
        infoWindowsRef.current = [];
        setMapMarkers([]);

        // Remove old polyline if it exists
        if (polylineRef.current) {
          polylineRef.current.setMap(null);
          polylineRef.current = null;
        }
      }

      // --- Get the route to display (skip Admin End) ---
      const routeToUse = optimizationRoute.length > 0 ? optimizationRoute : optimizationResult.route;
      const filteredRoute = routeToUse.filter(point => point.name !== 'Admin (End)');

      const geocoder = new window.google.maps.Geocoder();
      const newMarkers = [];
      const markerPositions = []; // track LatLng for polyline
      const bounds = new window.google.maps.LatLngBounds();

      // --- Process each point (with abort checks) ---
      for (let i = 0; i < filteredRoute.length; i++) {
        // Abort if a newer initialization was requested
        if (mapInitId.current !== initId) {
          return;
        }

        const point = filteredRoute[i];
        if (!point.address) continue;

        // --- Geocode with cache ---
        let location = geocodeCache.current[point.address];
        if (!location) {
          // Add a small delay between geocoding requests (helpful for mobile)
          if (i > 0) await new Promise(resolve => setTimeout(resolve, 50));

          const results = await geocodeAddress(geocoder, point.address);
          if (mapInitId.current !== initId) return; // abort after async call

          if (results && results.length > 0) {
            location = results[0].geometry.location;
            geocodeCache.current[point.address] = location; // cache it
          } else {
            console.warn(`Could not geocode address: ${point.address}`);
            continue;
          }
        }

        // --- Determine label and color ---
        let label = '';
        let color = '';
        if (i === 0) {
          label = 'A';
          color = '#333'; // Home
        } else {
          label = String.fromCharCode(65 + i); // B, C, D...
          color = '#fa4848';
        }

        // --- Create marker ---
        const pinEl = document.createElement('div');
        pinEl.style.cssText = [
          'width:28px', 'height:28px', 'border-radius:50%',
          `background:${color}`, 'border:2px solid white',
          'display:flex', 'align-items:center', 'justify-content:center',
          'color:white', 'font-weight:bold', 'font-size:12px',
          "font-family:Arial,sans-serif", 'box-shadow:0 2px 4px rgba(0,0,0,0.4)',
          'cursor:pointer',
        ].join(';');
        pinEl.textContent = label;
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: location,
          map: map,
          title: `${label}: ${point.name}`,
          content: pinEl,
          gmpClickable: true,
        });

        // --- Info window (using current point data) ---
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-family: Arial, sans-serif; max-width: 250px;">
              <div style="font-weight: bold; margin-bottom: 5px; color: #333;">
                ${label}: ${point.name}
              </div>
              <div style="font-size: 12px; color: #666; margin-bottom: 8px;">
                ${point.address}
              </div>
              ${point.toNext && point.toNext.distance !== 'N/A' && point.toNext.distance !== '-' ? `
                <div style="border-top: 1px solid #eee; padding-top: 5px; margin-top: 5px;">
                  <div style="font-size: 12px; color: #666;">
                    <strong>To next:</strong> ${point.toNext.distance}
                  </div>
                  <div style="font-size: 12px; color: #666;">
                    <strong>Time:</strong> ${point.toNext.duration}
                  </div>
                </div>
              ` : ''}
            </div>
          `
        });

        // 'gmp-click' for AdvancedMarkerElement-backed markers, 'click' for legacy
        const clickEvent = marker.element ? 'gmp-click' : 'click';
        marker.addListener(clickEvent, () => {
          infoWindowsRef.current.forEach(iw => iw.close());
          infoWindow.open({ anchor: marker, map });
        });

        newMarkers.push(marker);
        markerPositions.push(location);
        infoWindowsRef.current.push(infoWindow);
        bounds.extend(location);
      }

      // --- Store new markers in state ---
      setMapMarkers(newMarkers);

      // --- Draw polyline if we have at least two markers ---
      if (newMarkers.length >= 2) {
        const path = markerPositions;
        polylineRef.current = new window.google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: isDarkMode ? '#ffffff' : '#666',
          strokeOpacity: 0.9,
          strokeWeight: 3,
          map: map
        });
      }

      // --- Fit bounds to show all markers ---
      if (newMarkers.length > 0) {
        // Add a little padding
        const padding = 0.05;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latDiff = (ne.lat() - sw.lat()) * padding;
        const lngDiff = (ne.lng() - sw.lng()) * padding;

        bounds.extend(new window.google.maps.LatLng(ne.lat() + latDiff, ne.lng() + lngDiff));
        bounds.extend(new window.google.maps.LatLng(sw.lat() - latDiff, sw.lng() - lngDiff));

        map.fitBounds(bounds);

        // Limit zoom for single marker
        if (newMarkers.length === 1) {
          setTimeout(() => {
            if (map.getZoom() > 15) map.setZoom(15);
          }, 100);
        }
      }

      mapInitializedRef.current = true;
    } catch (error) {
      console.error('Error initializing map:', error);
    } finally {
      setMapLoading(false);
    }
  }, [optimizationResult, optimizationRoute, mapInstance, mapMarkers]); 

  // Initialize map when optimization modal opens
  useEffect(() => {
    if (optimizeModalOpen && optimizationResult) {
      const currentId = Date.now();
      mapInitId.current = currentId;

      // Clear previous timer
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        // If a newer initialization started, abort
        if (mapInitId.current !== currentId) return;

        const doInit = () => {
          if (!mapInitializedRef.current) {
            initMap(currentId); // pass the ID so initMap can abort itself
          }
        };

        if (window.google?.maps) {
          doInit();
        } else {
          loadGoogleMaps()
            .then(() => {
              if (mapInitId.current === currentId) doInit();
            })
            .catch(error => {
              console.error('Failed to load Google Maps:', error);
              setMapLoading(false);
            });
        }
      }, 300);

      return () => {
        clearTimeout(timerRef.current);
        // Clean up markers & info windows (keep this)
        mapMarkers.forEach(marker => { marker.map = null; });
        infoWindowsRef.current.forEach(iw => iw.close());
      };
    }
  }, [optimizeModalOpen, optimizationResult]);

  // Cleanup on component unmount or modal close
  useEffect(() => {
    return () => {
      // Clean up markers and info windows
      mapMarkers.forEach(marker => { marker.map = null; });
      infoWindowsRef.current.forEach(iw => iw.close());
      infoWindowsRef.current = [];
      mapInitializedRef.current = false;
    };
  }, [mapMarkers]);

  // Recalculate distances when route changes
  const recalculateDistances = async (route) => {
    try {
      const newRoute = [...route];
      
      // Calculate distances for all legs
      for (let i = 0; i < newRoute.length - 1; i++) {
        const fromAddress = newRoute[i].address;
        const toAddress = newRoute[i + 1].address;
        
        try {
          const [formattedOrigin, formattedDestination] = await Promise.all([
            formatAddressForDistance(fromAddress),
            formatAddressForDistance(toAddress)
          ]);
          
          if (formattedOrigin && formattedDestination) {
            const cacheKey = `${formattedOrigin}-${formattedDestination}`;
            
            if (distanceCache.current[cacheKey]) {
              newRoute[i].toNext = {
                distance: distanceCache.current[cacheKey].distance,
                duration: distanceCache.current[cacheKey].duration,
                distanceValue: distanceCache.current[cacheKey].distanceValue
              };
            } else {
              const result = await calculateDistanceWithGoogle(formattedOrigin, formattedDestination);
              newRoute[i].toNext = {
                distance: result.distance,
                duration: result.duration,
                distanceValue: result.distanceValue
              };
              
              distanceCache.current[cacheKey] = result;
            }
          } else {
            newRoute[i].toNext = {
              distance: 'N/A',
              duration: 'N/A',
              distanceValue: null
            };
          }
        } catch (error) {
          console.error(`Error recalculating distance from ${i} to ${i + 1}:`, error);
          newRoute[i].toNext = {
            distance: 'Error',
            duration: 'Error',
            distanceValue: null
          };
        }
      }

      // Update route and recalculate totals
      const totalDistance = newRoute.reduce((sum, point, index) => {
        if (index < newRoute.length - 1 && point.toNext && point.toNext.distanceValue) {
          return sum + point.toNext.distanceValue;
        }
        return sum;
      }, 0);

      const totalDuration = newRoute.reduce((sum, point, index) => {
        if (index < newRoute.length - 1 && point.toNext && point.toNext.duration) {
          const durationStr = point.toNext.duration;
          let minutes = 0;
          
          const hoursMatch = durationStr.match(/(\d+)\s*hour/);
          const minsMatch = durationStr.match(/(\d+)\s*min/);
          
          if (hoursMatch) minutes += parseInt(hoursMatch[1]) * 60;
          if (minsMatch) minutes += parseInt(minsMatch[1]);
          
          return sum + minutes;
        }
        return sum;
      }, 0);

      const newOptimizationResult = {
        totalDistance: (totalDistance / 1000).toFixed(2) + ' km',
        totalDuration: formatDuration(totalDuration),
        route: newRoute
      };

      setOptimizationResult(newOptimizationResult);
      
      // Update the optimizationRoute state
      setOptimizationRoute(newRoute);

      // Refresh the map with new data
      setTimeout(() => {
        if (window.google && window.google.maps && optimizeModalOpen) {
          mapInitializedRef.current = false;
          initMap();
        }
      }, 100);

    } catch (error) {
      console.error('Error recalculating distances:', error);
    }
  };

  // Draw straight lines between markers
  const drawPolyline = useCallback((map, markers) => {
    if (!window.google || !window.google.maps || markers.length < 2) return;
    
    // Create path from marker positions
    const path = markers.map(marker => marker.position);
    
    // Create polyline
    const polyline = new window.google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: document.documentElement.getAttribute('data-theme') === 'dark' ? '#ffffff' : '#666',
      strokeOpacity: 0.9,
      strokeWeight: 3,
      map: map
    });
    
    return polyline;
  }, []);

  // Close optimization modal
  const closeOptimizationModal = () => {
    // Detach markers so Google Maps stops owning those DOM nodes
    mapMarkers.forEach(marker => { marker.map = null; });
    infoWindowsRef.current.forEach(iw => iw.close());
    infoWindowsRef.current = [];

    // Clear the map container's children BEFORE React unmounts the div.
    // Skipping this causes the 'removeChild' NotFoundError because Google Maps
    // injects its own DOM nodes that React's fiber tree doesn't know about.
    if (mapContainerRef.current) {
      mapContainerRef.current.innerHTML = '';
    }

    setOptimizeModalOpen(false);
    setOptimizationRoute([]);
    setOptimizationResult(null);
    setIsDragging(null);
    setMapMarkers([]);
    setMapInstance(null);
    mapInitializedRef.current = false;
    setMapLoading(false);
  };
  // Convert Google Drive URL to direct image URL using proxy like OrderHistory
  const convertGoogleDriveUrl = (url) => {
    if (!url) return null;

    // If it's already a proxy URL, return it as is
    if (url.includes('us-central1-pcakedatastore.cloudfunctions.net/driveImageProxy')) {
      return url;
    }

    // If it's a Google Drive URL, convert it to proxy URL
    if (url.includes('drive.google.com')) {
      const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)|id=([a-zA-Z0-9-_]+)/);
      if (!fileIdMatch) return null;

      const fileId = fileIdMatch[1] || fileIdMatch[2];
      if (!fileId) return null;

      // Use the same proxy server as OrderHistory
      return `https://us-central1-pcakedatastore.cloudfunctions.net/driveImageProxy?id=${fileId}&sz=w800`;
    }

    // If it's some other URL that starts with http/https, return it as is
    if (url.startsWith('http')) {
      return url;
    }

    return null;
  };

  // Get city from German postal code using Zippopotam.us
  const getCityFromGermanPostalCode = useCallback(async (postalCode) => {
    if (!postalCode) return null;
    
    // Clean postal code (5 digits)
    const cleanPostalCode = postalCode.toString().match(/\b(\d{5})\b/);
    if (!cleanPostalCode) return null;
    
    const code = cleanPostalCode[1];
    
    // Check cache first
    if (postalCodeCache.current[code]) {
      return postalCodeCache.current[code];
    }
    
    try {
      const response = await fetch(`https://api.zippopotam.us/de/${code}`);
      
      if (!response.ok) {
        console.warn(`Postal code ${code} not found in Zippopotam`);
        postalCodeCache.current[code] = null;
        return null;
      }
      
      const data = await response.json();
      
      if (data.places && data.places.length > 0) {
        const city = data.places[0]['place name'];
        
        // Cache the result
        postalCodeCache.current[code] = city;
        return city;
      }
    } catch (error) {
      console.error('Error fetching postal code data:', error);
      return null;
    }
    
    return null;
  }, []);

  // Extract postal code from address
  const extractGermanPostalCode = (address) => {
    if (!address) return null;
    
    const addressStr = address.toString().trim();
    
    // Look for 5-digit German postal code
    const postalCodeMatch = addressStr.match(/\b(\d{5})\b/);
    if (postalCodeMatch) {
      return postalCodeMatch[1];
    }
    
    return null;
  };

  // Format German address for Google Maps
  const formatGermanAddressForGoogle = useCallback(async (address) => {
    if (!address) return null;
    
    let cleanAddress = address.trim();
    
    // Try to extract postal code
    const postalCode = extractGermanPostalCode(cleanAddress);
    
    if (postalCode) {
      // Get city from postal code
      const city = await getCityFromGermanPostalCode(postalCode);
      
      if (city) {
        // Replace postal code with city name for better Google Maps results
        // Keep the full address but ensure city is included
        let formattedAddress = cleanAddress;
        
        // If address doesn't already contain the city name, add it
        if (!formattedAddress.toLowerCase().includes(city.toLowerCase())) {
          // Try to intelligently insert city
          const postalCodePattern = new RegExp(`\\b${postalCode}\\b`);
          if (postalCodePattern.test(formattedAddress)) {
            // Replace postal code with "City, Germany"
            formattedAddress = formattedAddress.replace(postalCodePattern, `${city}, Germany`);
          } else {
            // Just append city and country
            formattedAddress = `${formattedAddress}, ${city}, Germany`;
          }
        } else if (!formattedAddress.toLowerCase().includes('germany') && 
                   !formattedAddress.toLowerCase().includes('deutschland')) {
          // Address has city but not country
          formattedAddress = `${formattedAddress}, Germany`;
        }
        
        return formattedAddress;
      }
    }
    
    // Fallback: add Germany if not present
    if (!cleanAddress.toLowerCase().includes('germany') && 
        !cleanAddress.toLowerCase().includes('deutschland')) {
      return `${cleanAddress}, Germany`;
    }
    
    return cleanAddress;
  }, [getCityFromGermanPostalCode]);

  // Load Google Maps API
  const loadGoogleMaps = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Already fully loaded (including marker library)
      if (window.google && window.google.maps && window.google.maps.marker) {
        googleMapsLoaded.current = true;
        resolve();
        return;
      }

      if (!GOOGLE_MAPS_API_KEY) {
        reject(new Error('Google Maps API key is not configured'));
        return;
      }

      // Remove any existing Maps script that may be missing the marker library
      const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existing) existing.remove();

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        googleMapsLoaded.current = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });
  }, []);

  // Calculate distance via backend proxy — API key stays server-side
  const calculateDistanceWithGoogle = useCallback(async (origin, destination) => {
    const backendUrl = getBackendUrl();
    const res = await fetch(`${backendUrl}/distance-matrix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ origin, destination }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Distance API error ${res.status}`);
    }
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'Distance calculation failed');
    }
    return { distance: data.distance, distanceValue: data.distanceValue, duration: data.duration };
  }, []);

  // Function to validate image URL before using it (same as OrderHistory)
  const validateImageUrl = (url) => {
    if (!url) return false;
    
    // Skip if it's not a proper URL or doesn't start with expected patterns
    if (!url.startsWith('http') && !url.startsWith('//')) {
      return false;
    }
    
    // Skip Google Forms URLs
    if (url.includes('forms_web') || url.includes('usp=forms')) {
      return false;
    }

    // Accept both proxy URLs and direct URLs
    return true;
  };

  // Enhanced address formatter with city lookup
  const formatAddressForDistance = useCallback(async (address, useCorrected = false) => {
    if (!address) return null;
    
    // If it's already a corrected address that has been working, use it as-is
    if (useCorrected && (address.includes(', Germany') || address.includes(', Deutschland'))) {
      return address;
    }
    
    // Try German address formatting first
    const germanFormatted = await formatGermanAddressForGoogle(address);
    if (germanFormatted) {
      return germanFormatted;
    }
    
    // Fallback: add Germany if not present
    let cleanAddress = address.trim();
    if (!cleanAddress.toLowerCase().includes('germany') && 
        !cleanAddress.toLowerCase().includes('deutschland')) {
      cleanAddress += ', Germany';
    }
    
    return cleanAddress;
  }, [formatGermanAddressForGoogle]);

  // Calculate distance for a single order
  const calculateOrderDistance = useCallback(async (order) => {
    if (!adminAddress) {
      return { ...order, distance: 'N/A' };
    }
    
    // Use corrected address if available, otherwise use original address
    const destinationAddress = order.correctedAddress || order.address;
    
    if (!destinationAddress) {
      return { ...order, distance: 'No address' };
    }
    
    try {
      // Format both addresses
      const [formattedOrigin, formattedDestination] = await Promise.all([
        formatAddressForDistance(adminAddress),
        formatAddressForDistance(destinationAddress, !!order.correctedAddress)
      ]);
      
      if (!formattedOrigin || !formattedDestination) {
        console.warn('Invalid addresses:', { 
          origin: adminAddress, 
          destination: destinationAddress,
          orderId: order.id 
        });
        return { ...order, distance: 'Invalid address' };
      }
      
      // Check cache
      const cacheKey = `${formattedOrigin}-${formattedDestination}`;
      if (distanceCache.current[cacheKey]) {
        return { 
          ...order, 
          distance: distanceCache.current[cacheKey].distance,
          distanceValue: distanceCache.current[cacheKey].distanceValue,
          duration: distanceCache.current[cacheKey].duration
        };
      }
      
      
      const result = await calculateDistanceWithGoogle(formattedOrigin, formattedDestination);
      
      // Cache the result
      distanceCache.current[cacheKey] = result;
      
      return { 
        ...order, 
        distance: result.distance,
        distanceValue: result.distanceValue,
        duration: result.duration
      };
    } catch (error) {
      console.error('Distance calculation failed:', error);
      return { ...order, distance: 'Error', distanceValue: null, duration: 'N/A' };
    }
  }, [adminAddress, calculateDistanceWithGoogle, formatAddressForDistance]);

  // Process all orders with distances
  const processOrdersWithDistances = useCallback(async (ordersList) => {
    if (!adminAddress || calculationInProgress.current || ordersList.length === 0) return;
    abortCalculation.current = false;
    const calculationId = Date.now();
    currentCalculationId.current = calculationId;
    calculationInProgress.current = true;
    setLoadingDistances(true);

    try {
      await loadGoogleMaps();

      const batchSize = 3;
      let aborted = false;

      for (let i = 0; i < ordersList.length; i += batchSize) {
        // Abort check
        if (abortCalculation.current) {
          aborted = true;
          break;
        }

        const batch = ordersList.slice(i, i + batchSize);

        // Mark whole batch as "Calculating..." in one state update
        setFilteredOrders(prev => {
          let changed = false;
          const updated = [...prev];
          for (const order of batch) {
            const idx = updated.findIndex(o => o.id === order.id);
            if (idx !== -1 && !updated[idx].distanceValue && updated[idx].distance !== 'Calculating...') {
              updated[idx] = { ...updated[idx], distance: 'Calculating...' };
              changed = true;
            }
          }
          return changed ? updated : prev;
        });

        // Small delay to allow state updates to settle (optional)
        await new Promise(resolve => setTimeout(resolve, 50));

        // Calculate distances for the batch
        const batchPromises = batch.map(async (order) => {
          // Re-fetch latest order state from ref to avoid stale data
          const latestOrder = filteredOrdersRef.current.find(o => o.id === order.id);
          if (latestOrder && latestOrder.distanceValue != null && 
              latestOrder.distance !== 'Error' && 
              latestOrder.distance !== 'Invalid address' && 
              latestOrder.distance !== 'No address') {
            return latestOrder;
          }
          const result = await calculateOrderDistance(order);
          return result;
        });

        const batchResults = await Promise.allSettled(batchPromises);

        // Abort check after batch
        if (currentCalculationId.current !== calculationId) {
          aborted = true;
          break;
        }

        // Update all batch results in one state update
        setFilteredOrders(prev => {
          let changed = false;
          const updated = [...prev];
          for (let batchIdx = 0; batchIdx < batchResults.length; batchIdx++) {
            const result = batchResults[batchIdx];
            if (result.status === 'fulfilled' && result.value) {
              const idx = updated.findIndex(o => o.id === batch[batchIdx].id);
              if (idx !== -1) {
                updated[idx] = result.value;
                changed = true;
              }
            } else if (result.status === 'rejected') {
              console.error(`[${calculationId}] Batch promise rejected:`, result.reason);
            }
          }
          return changed ? updated : prev;
        });

        // Delay between batches
        if (i + batchSize < ordersList.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (aborted) {
        return;
      }

      // After all batches, check if any orders still need calculation
      const currentOrders = filteredOrdersRef.current;
      const stillPending = currentOrders.filter(o => 
        !o.distanceValue && 
        o.distance !== 'Error' && 
        o.distance !== 'Invalid address' && 
        o.distance !== 'No address'
      ).length;
      if (stillPending > 0) {
        setRetryTrigger(prev => prev + 1);
      } 

    } catch (error) {
      console.error(`[${calculationId}] ERROR:`, error);
    } finally {
      if (currentCalculationId.current === calculationId) {
        currentCalculationId.current = null;
        calculationInProgress.current = false;
        setLoadingDistances(false);
      } else {
        calculationInProgress.current = false;
        setLoadingDistances(false);
      }
    }
  }, [adminAddress, loadGoogleMaps, calculateOrderDistance]);

  // FIXED: Save address function with auto-save on blur (no optional chaining assignment)
  const saveAddress = async () => {
    if (!user || !db || !customerModal || addressLoading || !editedAddress.trim()) return;
    
    try {
      setAddressLoading(true);
      const { doc, setDoc } = await import('firebase/firestore');
      const targetCollection = customerModal._isAppOrder ? 'order' : 'FormOrders';
      const docRef = doc(db, targetCollection, customerModal.id);
      // App orders use camelCase field names; FormOrders use PascalCase (Google Forms convention)
      const addressField = customerModal._isAppOrder ? 'correctedAddress' : 'CorrectedAddress';
      
      await setDoc(docRef, { 
        [addressField]: editedAddress.trim(),
        updated: new Date().toISOString()
      }, { merge: true });
      
      // Update the local state
      const updateOrderInArray = (array) => array.map(order => {
        if (order.id === customerModal.id) {
          const updatedRawData = { ...order.rawData };
          updatedRawData.CorrectedAddress = editedAddress.trim();
          
          const updatedOrder = { 
            ...order, 
            correctedAddress: editedAddress.trim(),
            rawData: updatedRawData,
            distance: 'Recalculating...'
          };
          return updatedOrder;
        }
        return order;
      });
      
      setOrders(prevOrders => updateOrderInArray(prevOrders));
      setFilteredOrders(prevFiltered => updateOrderInArray(prevFiltered));
      
      // Update the customerModal to reflect the change
      setCustomerModal(prev => {
        const newData = { ...prev };
        newData.correctedAddress = editedAddress.trim();
        newData.rawData = { ...prev.rawData };
        newData.rawData.CorrectedAddress = editedAddress.trim();
        return newData;
      });
      
      // Recalculate distance
      const updatedOrder = updateOrderInArray(orders).find(o => o.id === customerModal.id);
      if (updatedOrder) {
        const recalculated = await calculateOrderDistance(updatedOrder);
        
        // Update the order with new distance
        const updateWithNewDistance = (array) => array.map(order => 
          order.id === customerModal.id ? recalculated : order
        );
        
        setOrders(prevOrders => updateWithNewDistance(prevOrders));
        setFilteredOrders(prevFiltered => updateWithNewDistance(prevFiltered));
        
        // Update modal
        setCustomerModal(prev => {
          const newData = { ...prev };
          newData.distance = recalculated.distance;
          newData.duration = recalculated.duration;
          return newData;
        });
      }
      
      setAddressEditing(false);
      
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setAddressLoading(false);
    }
  };

  // Delete corrected address function
  const deleteCorrectedAddress = async () => {
    if (!user || !db || !customerModal) return;
    
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const targetCollection = customerModal._isAppOrder ? 'order' : 'FormOrders';
      const docRef = doc(db, targetCollection, customerModal.id);
      const addressField = customerModal._isAppOrder ? 'correctedAddress' : 'CorrectedAddress';
      
      await setDoc(docRef, { 
        [addressField]: '',
        updated: new Date().toISOString()
      }, { merge: true });
      
      // Update the local state
      const updateOrderInArray = (array) => array.map(order => {
        if (order.id === customerModal.id) {
          const updatedRawData = { ...order.rawData };
          updatedRawData.CorrectedAddress = '';
          
          const updatedOrder = { 
            ...order, 
            correctedAddress: '',
            rawData: updatedRawData,
            distance: 'Recalculating...'
          };
          return updatedOrder;
        }
        return order;
      });
      
      setOrders(prevOrders => updateOrderInArray(prevOrders));
      setFilteredOrders(prevFiltered => updateOrderInArray(prevFiltered));
      
      // Update the customerModal
      setCustomerModal(prev => {
        const newData = { ...prev };
        newData.correctedAddress = '';
        newData.rawData = { ...prev.rawData };
        newData.rawData.CorrectedAddress = '';
        return newData;
      });
      
      // Recalculate with original address
      const updatedOrder = updateOrderInArray(orders).find(o => o.id === customerModal.id);
      if (updatedOrder) {
        const recalculated = await calculateOrderDistance(updatedOrder);
        
        const updateWithNewDistance = (array) => array.map(order => 
          order.id === customerModal.id ? recalculated : order
        );
        
        setOrders(prevOrders => updateWithNewDistance(prevOrders));
        setFilteredOrders(prevFiltered => updateWithNewDistance(prevFiltered));
        
        setCustomerModal(prev => {
          const newData = { ...prev };
          newData.distance = recalculated.distance;
          newData.duration = recalculated.duration;
          return newData;
        });
      }
      
    } catch (error) {
      console.error('Error deleting address:', error);
    }
  };

  // Fetch admin address
  const fetchAdminAddress = useCallback(async () => {
    try {
      const cachedAdminAddress = localStorage.getItem('organize_admin_address');
      if (cachedAdminAddress) {
        setAdminAddress(cachedAdminAddress);
        return;
      }

      const userList = await readGlobalProfiles('cUsers');
      const adminUser = userList.find(user => user.isadminuser === true);
      
      if (!adminUser) {
        console.warn('No admin user found in cUsers');
        const defaultAddress = 'Germany';
        setAdminAddress(defaultAddress);
        localStorage.setItem('organize_admin_address', defaultAddress);
        return;
      }

      const profileDataList = await readUserCollection('profile', adminUser.id);
      const adminProfile = profileDataList.length > 0 ? profileDataList[0] : null;
      
      if (adminProfile && adminProfile.address) {
        setAdminAddress(adminProfile.address);
        localStorage.setItem('organize_admin_address', adminProfile.address);
      } else {
        const address = adminProfile?.location || adminProfile?.street || adminProfile?.city || 'Germany';
        setAdminAddress(address);
        localStorage.setItem('organize_admin_address', address);
      }
      
    } catch (error) {
      console.error('Error fetching admin address:', error);
      const defaultAddress = 'Germany';
      setAdminAddress(defaultAddress);
      localStorage.setItem('organize_admin_address', defaultAddress);
    }
  }, []);

  // Initialize modal data
  useEffect(() => {
    if (customerModal && (userRole === 'Admin' || userRole === 'Super' || userRole === 'Mover')) {
      const cost = customerModal.cost || '';
      const numericValue = cost.replace(' �', '').replace('�', '').trim();
      setPriceInput(numericValue);
      
      const correctedAddress = customerModal.correctedAddress || '';
      setEditedAddress(correctedAddress);
      setAddressEditing(false);
    } else {
      setPriceInput('');
      setEditedAddress('');
      setAddressEditing(false);
    }
  }, [customerModal, userRole]);

  // Parse date from DD.MM.YYYY format
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    
    try {
      if (dateStr.includes('.')) {
        const parts = dateStr.split('.');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        }
      } else if (dateStr.includes('-')) {
        return new Date(dateStr);
      }
    } catch (error) {
      console.error('Error parsing date:', error);
    }
    return null;
  };

  // Format date for table display
  const formatTableDate = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Fetch FormOrderOld data with Google Drive URL conversion
  const fetchOrders = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const ordersList = await readGlobalProfiles('FormOrders');
      
      const formDeliveryOrders = ordersList
        .map(doc => {
          const data = doc.data || doc;
          
          const deliveryMode = data['Delivery or Pick-Up'] || '';
          if (deliveryMode.toLowerCase().includes('delivery')) {
            const deliveryDateStr = data['Delivery/Pick-Up Date'];
            const deliveryTime = data['Delivery Time'] || '';
            const name = data['Contact Person Full Name'] || '';
            const address = data['Address'] || '';
            const correctedAddress = data['CorrectedAddress'] || '';
            const imageUrls = data['Upload Wished Design (Max 10 MB or 5 Files)'] || '';
            
            // Convert Google Drive URLs to direct image URLs
            let image = null;
            if (imageUrls) {
              const firstUrl = imageUrls.split(',')[0];
              const convertedUrl = convertGoogleDriveUrl(firstUrl);
              // Validate the URL before using it
              if (convertedUrl && validateImageUrl(convertedUrl)) {
                image = convertedUrl;
              }
            }

            return {
              id: doc.id || doc.uid || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name,
              address,
              correctedAddress,
              deliveryDate: deliveryDateStr,
              deliveryTime,
              image,
              rawData: data,
              distance: 'Pending...',
              distanceValue: null,
              duration: 'N/A'
            };
          }
          return null;
        })
        .filter(order => order !== null);

      // Also fetch app orders from the 'order' Firestore collection
      let appDeliveryOrders = [];
      try {
        const appSnap = await getDocs(collection(db, 'order'));
        appDeliveryOrders = appSnap.docs
          .map(d => {
            const data = d.data();
            // Skip cancelled, soft-deleted, or pick-up orders
            if (data.s_delete || data.reqState === 'Order Cancelled') return null;
            if (data.del_mode === false) return null; // pick-up order

            const uid = data.userId || '';
            const userInfo = userProfileMapRef.current[uid] || {};

            // Parse deliveryDate (Firestore Timestamp, Date, or string)
            const rawDate = data.deliveryDate;
            let deliveryDateStr = '';
            if (rawDate) {
              if (typeof rawDate === 'object' && typeof rawDate.toDate === 'function') {
                const dt = rawDate.toDate();
                const y = dt.getFullYear();
                const m = String(dt.getMonth() + 1).padStart(2, '0');
                const day = String(dt.getDate()).padStart(2, '0');
                deliveryDateStr = `${y}-${m}-${day}`;
              } else if (rawDate instanceof Date) {
                const y = rawDate.getFullYear();
                const m = String(rawDate.getMonth() + 1).padStart(2, '0');
                const day = String(rawDate.getDate()).padStart(2, '0');
                deliveryDateStr = `${y}-${m}-${day}`;
              } else {
                deliveryDateStr = String(rawDate);
              }
            }

            // Pick first image from the images array
            let image = null;
            if (Array.isArray(data.images) && data.images.length > 0) {
              image = data.images[0] || null;
            }

            // Compute cost string from ccost + del_cost (same logic as Order.jsx)
            const cakeCost = data.ccost != null ? parseFloat(String(data.ccost)) : null;
            const delCost = data.del_cost != null ? parseFloat(String(data.del_cost)) : null;
            let costStr = '';
            if (cakeCost != null) {
              const total = delCost != null ? cakeCost + delCost : cakeCost;
              costStr = `${total} €`;
            }

            return {
              id: d.id,
              _isAppOrder: true,
              _delCost: delCost ?? 0,
              name: userInfo.name || 'N/A',
              phone: userInfo.phone || '',
              address: data.del_address || '',
              // Read correctedAddress for app orders (camelCase field written by Organize.jsx)
              correctedAddress: data.correctedAddress || data.CorrectedAddress || '',
              deliveryDate: deliveryDateStr,
              deliveryTime: data.deliveryTime || '',
              cost: costStr,
              image,
              // rawData kept empty so that source-collection detection (via 'E-Mail-Address') works correctly
              rawData: {},
              distance: 'Pending...',
              distanceValue: null,
              duration: 'N/A'
            };
          })
          .filter(o => o !== null);
      } catch (appErr) {
        console.error('Organize: error fetching app orders:', appErr);
      }

      const deliveryOrders = [...formDeliveryOrders, ...appDeliveryOrders];

      // Sort by delivery date then time
      deliveryOrders.sort((a, b) => {
        const dateA = a.deliveryDate ? parseDate(a.deliveryDate) : new Date(0);
        const dateB = b.deliveryDate ? parseDate(b.deliveryDate) : new Date(0);
        
        const dateComparison = dateA - dateB;
        if (dateComparison !== 0) {
          return dateComparison;
        }
        
        const timeA = a.deliveryTime || '';
        const timeB = b.deliveryTime || '';
        return timeA.localeCompare(timeB);
      });

      setOrders(deliveryOrders);
      setFilteredOrders(deliveryOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      await fetchAdminAddress();
      await fetchOrders();
    };
    
    fetchData();
  }, [fetchAdminAddress, fetchOrders]);

  useEffect(() => {
    filteredOrdersRef.current = filteredOrders;
  }, [filteredOrders]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Use ref instead of filteredOrders state to avoid re-triggering on every per-order update
    const orders = filteredOrdersRef.current;
    if (adminAddress && orders.length > 0 && !calculationInProgress.current) {
      const needsDistanceCalculation = orders.some(
        order => !order.distanceValue ||
                  order.distance === 'Pending...' ||
                  order.distance === 'Calculating...' ||
                  order.distance === 'Recalculating...'
      );
      if (needsDistanceCalculation) {
        timerRef.current = setTimeout(() => {
          processOrdersWithDistances(filteredOrdersRef.current);
        }, 500);
      }
    }
    return () => {
      clearTimeout(timerRef.current);
      abortCalculation.current = false;
    };
  // pendingCalcSignal fires when the sort effect resets order distances to 'Pending...'
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAddress, pendingCalcSignal, processOrdersWithDistances, retryTrigger]);

  // Filter and sort orders - FIXED VERSION
  useEffect(() => {
    if (!orders.length) return;

    const filterAndProcessOrders = async () => {
      // First filter
      const filtered = orders.filter(order => {
        const orderDate = parseDate(order.deliveryDate);
        if (!orderDate) return false;

        const current = new Date(currentDate);
        current.setHours(0, 0, 0, 0);
        
        switch (viewMode) {
          case 'month': {
            return orderDate.getMonth() === current.getMonth() && 
                  orderDate.getFullYear() === current.getFullYear();
          }

          case 'week': {
            const weekStart = new Date(current);
            const day = weekStart.getDay();
            const diff = day === 0 ? 6 : day - 1;
            weekStart.setDate(weekStart.getDate() - diff);
            weekStart.setHours(0, 0, 0, 0);
            
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);
            weekEnd.setHours(23, 59, 59, 999);
            
            return orderDate >= weekStart && orderDate <= weekEnd;
          }

          case 'day': {
            const dayStart = new Date(current);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(current);
            dayEnd.setHours(23, 59, 59, 999);
            
            return orderDate >= dayStart && orderDate <= dayEnd;
          }

          default:
            return true;
        }
      });

      // Pre-build date cache so parseDate is only called once per order, not per comparison
      const dateCache = new Map();
      const getCachedDate = (dateStr) => {
        if (!dateCache.has(dateStr)) {
          dateCache.set(dateStr, dateStr ? parseDate(dateStr) : new Date(0));
        }
        return dateCache.get(dateStr);
      };

      // Then sort
      const sorted = [...filtered].sort((a, b) => {
        let primaryComparison = 0;
        let secondaryComparison = 0;
        
        switch (sortConfig.key) {
          case 'name':
            primaryComparison = (a.name || '').localeCompare(b.name || '');
            break;
            
          case 'deliveryDate':
            primaryComparison = getCachedDate(a.deliveryDate) - getCachedDate(b.deliveryDate);
            break;
            
          case 'deliveryTime':
            primaryComparison = (a.deliveryTime || '').localeCompare(b.deliveryTime || '');
            break;
            
          case 'distanceValue':
            primaryComparison = (a.distanceValue || Infinity) - (b.distanceValue || Infinity);
            break;
            
          default:
            primaryComparison = 0;
        }
        
        primaryComparison *= (sortConfig.direction === 'asc' ? 1 : -1);
        
        if (primaryComparison === 0 && sortConfig.key !== sortConfig.secondaryKey) {
          switch (sortConfig.secondaryKey) {
            case 'name':
              secondaryComparison = (a.name || '').localeCompare(b.name || '');
              break;
              
            case 'deliveryDate':
              secondaryComparison = getCachedDate(a.deliveryDate) - getCachedDate(b.deliveryDate);
              break;
              
            case 'deliveryTime':
              secondaryComparison = (a.deliveryTime || '').localeCompare(b.deliveryTime || '');
              break;
              
            case 'distanceValue':
              secondaryComparison = (a.distanceValue || Infinity) - (b.distanceValue || Infinity);
              break;
              
            default:
              secondaryComparison = 0;
          }
          
          secondaryComparison *= (sortConfig.secondaryDirection === 'asc' ? 1 : -1);
          return secondaryComparison;
        }
        
        return primaryComparison;
      });

      setFilteredOrders(sorted.map(order => ({
        ...order,
        distance: 'Pending...',
        distanceValue: null,
        duration: 'N/A'
      })));
      // Signal the distance effect to run (avoids depending on filteredOrders state directly)
      setPendingCalcSignal(s => s + 1);
    };

    filterAndProcessOrders();
  }, [orders, viewMode, currentDate, sortConfig, adminAddress]);

  const cleanupBeforeNavigation = () => {
    // Clear all selected checkboxes
    setSelectedOrders(new Set());
    
    // Close customer modal if open
    if (customerModal) {
      setCustomerModal(null);
      document.body.style.overflow = 'auto';
    }
    
    // Close optimization modal if open
    if (optimizeModalOpen) {
      setOptimizeModalOpen(false);
      setOptimizationRoute([]);
      setOptimizationResult(null);
      setIsDragging(null);
    }
    
    // Close image zoom modal if open
    if (zoomModalOpen) {
      setZoomModalOpen(false);
    }
    
    // Reset any edit states
    setPriceInput('');
    setEditedAddress('');
    setAddressEditing(false);
  };

  // Navigation handlers
  // Returns true when going back one period would land entirely before today
  const isPreviousDisabled = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const newDate = new Date(currentDate);
    switch (viewMode) {
      case 'day': {
        newDate.setDate(newDate.getDate() - 1);
        newDate.setHours(0, 0, 0, 0);
        return newDate < today;
      }
      case 'week': {
        newDate.setDate(newDate.getDate() - 7);
        // Find Monday of that week (same logic as the order filter)
        const d = newDate.getDay();
        const diffToMon = d === 0 ? 6 : d - 1;
        const weekStart = new Date(newDate);
        weekStart.setDate(weekStart.getDate() - diffToMon);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        return weekEnd < today;
      }
      case 'month': {
        newDate.setMonth(newDate.getMonth() - 1);
        // Last day of that month
        const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
        return lastDay < today;
      }
      default:
        return false;
    }
  };

  const handlePrevious = () => {
    if (isPreviousDisabled()) return;
    // Clear selections and close modals first
    cleanupBeforeNavigation();
    
    const newDate = new Date(currentDate);
    
    switch (viewMode) {
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
    }
    
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    // Clear selections and close modals first
    cleanupBeforeNavigation();
    
    const newDate = new Date(currentDate);
    
    switch (viewMode) {
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
    }
    
    setCurrentDate(newDate);
  };

  const handleReset = () => {
    setCurrentDate(new Date());
    setSelectedOrders(new Set());
    setSortConfig({ 
      key: 'deliveryDate', 
      direction: 'asc',
      secondaryKey: 'deliveryTime',
      secondaryDirection: 'asc'
    });
  };

  const handleViewChange = (mode) => {
    setViewMode(mode);
    setShowViewDropdown(false);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Toggle direction if same key
        const newConfig = { ...prevConfig };
        newConfig.direction = prevConfig.direction === 'asc' ? 'desc' : 'asc';
        return newConfig;
      } else {
        // New primary key, move old primary to secondary
        return {
          key: key,
          direction: 'asc',
          secondaryKey: prevConfig.key,
          secondaryDirection: prevConfig.direction
        };
      }
    });
  };

  const handleMoveUp = (itemId) => {
    const currentIndex = optimizationRoute.findIndex(p => p.id === itemId);
    if (currentIndex <= 1 || optimizationRoute[currentIndex].type === 'admin') return;

    const newRoute = [...optimizationRoute];
    [newRoute[currentIndex], newRoute[currentIndex - 1]] = 
      [newRoute[currentIndex - 1], newRoute[currentIndex]];

    setOptimizationRoute(newRoute);
    setOptimizationResult(prev => ({
      ...prev,
      route: newRoute,
      totalDistance: 'Recalculating...',
      totalDuration: 'Recalculating...'
    }));

    recalculateDistances(newRoute);
  };

  const handleImageClick = (imageUrl) => {
    if (imageUrl) {
      setSelectedImageUrl(imageUrl);
      setZoomModalOpen(true);
    }
  };

  const handleCustomerModalClick = (order) => {
    const rawData = order.rawData || {};
    const customerData = {
      id: order.id,
      name: order.name || 'N/A',
      phone: order.phone || rawData['Local Phone Number'] || 'N/A',
      address: order.address || 'N/A',
      correctedAddress: order.correctedAddress || '',
      cost: order.cost || rawData.Cost || '',
      distance: order.distance || 'N/A',
      duration: order.duration || 'N/A',
      rawData: rawData,
      _isAppOrder: order._isAppOrder || false,
      _delCost: order._delCost ?? null,
    };
    
    setCustomerModal(customerData);
    document.body.style.overflow = 'hidden';
  };

  const closeCustomerModal = () => {
    setCustomerModal(null);
    setEditedAddress('');
    setAddressEditing(false);
    document.body.style.overflow = 'auto';
  };

  const savePrice = async () => {
    if (!user || !db || !customerModal || priceLoading || !priceInput.trim()) return;
    
    try {
      setPriceLoading(true);
      const { doc, setDoc } = await import('firebase/firestore');
      const targetCollection = customerModal._isAppOrder ? 'order' : 'FormOrders';
      const docRef = doc(db, targetCollection, customerModal.id);

      let displayCost = '';
      if (customerModal._isAppOrder) {
        // For app orders: input is the total; store only cake portion (total - del_cost) in ccost
        const inputNum = parseFloat(priceInput.trim());
        const delCostNum = typeof customerModal._delCost === 'number'
          ? customerModal._delCost
          : parseFloat(customerModal._delCost || '0') || 0;
        const ccostVal = !isNaN(inputNum) ? String(inputNum - delCostNum) : priceInput.trim();
        await setDoc(docRef, { ccost: ccostVal, updated: new Date().toISOString() }, { merge: true });
        displayCost = `${priceInput.trim()} €`;
      } else {
        const priceWithEuro = `${priceInput.trim()} €`;
        await setDoc(docRef, { Cost: priceWithEuro, updated: new Date().toISOString() }, { merge: true });
        displayCost = priceWithEuro;
      }

      // Update the local state
      const updateOrderInArray = (array) => array.map(order => {
        if (order.id === customerModal.id) {
          const updatedRawData = { ...order.rawData };
          if (!customerModal._isAppOrder) updatedRawData.Cost = displayCost;
          return { ...order, cost: displayCost, rawData: updatedRawData };
        }
        return order;
      });
      
      setOrders(prevOrders => updateOrderInArray(prevOrders));
      setFilteredOrders(prevFiltered => updateOrderInArray(prevFiltered));
      
      // Update the customerModal to reflect the change
      setCustomerModal(prev => {
        const newData = { ...prev };
        newData.cost = displayCost;
        if (!customerModal._isAppOrder) {
          newData.rawData = { ...prev.rawData };
          newData.rawData.Cost = displayCost;
        }
        return newData;
      });
      
    } catch (error) {
      console.error('Error saving price:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  const getWeekRange = () => {
    const current = new Date(currentDate);
    const day = current.getDay();
    const diff = day === 0 ? 6 : day - 1;
    
    const weekStart = new Date(current);
    weekStart.setDate(weekStart.getDate() - diff);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    const startStr = weekStart.getDate();
    const startMonth = weekStart.toLocaleString('en-US', { month: 'short' });
    const endStr = weekEnd.getDate();
    const endMonth = weekEnd.toLocaleString('en-US', { month: 'short' });
    
    return `${startStr} ${startMonth} - ${endStr} ${endMonth}`;
  };

  const formatPeriodLabel = () => {
    switch (viewMode) {
      case 'month':
        return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'week':
        return getWeekRange();
      case 'day':
        return currentDate.toLocaleDateString('en-GB', { 
          weekday: 'short', 
          day: 'numeric', 
          month: 'short' 
        });
      default:
        return '';
    }
  };

  const openInGoogleMaps = (address) => {
    if (!address || address === 'N/A') return;
    
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  // Function to lookup postal code and suggest city
  const suggestCityFromPostalCode = async () => {
    if (!editedAddress) return;
    
    const postalCode = extractGermanPostalCode(editedAddress);
    if (postalCode) {
      try {
        const city = await getCityFromGermanPostalCode(postalCode);
        if (city) {
          // Auto-format the address with city
          let newAddress = editedAddress;
          const postalCodePattern = new RegExp(`\\b${postalCode}\\b`);
          
          if (postalCodePattern.test(newAddress)) {
            // Replace postal code with city
            newAddress = newAddress.replace(postalCodePattern, city);
          }
          
          // Ensure Germany is included
          if (!newAddress.toLowerCase().includes('germany') && 
              !newAddress.toLowerCase().includes('deutschland')) {
            newAddress = `${newAddress}, Germany`;
          }
          
          setEditedAddress(newAddress);
        }
      } catch (error) {
        console.error('Error suggesting city:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="organize-page">
        <div className="organize-loading">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="organize-page">
        <div className="organize-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="organize-page">
      <div className="organize-page-content">
        <div className="organize-page-body">
          {/* Header Section */}
          <div className="organize-header">
            {!activeAnalysis && (
            <div className="organize-header-buttons">
              <button
                className="organize-reset-btn"
                onClick={handleReset}
              >
                <FaRedo /> Reset
              </button>
              
              <button
                className="organize-optimize-btn"
                onClick={calculateOptimizedRoute}
              >
                <FaRoute /> {selectedOrders.size === 0 ? 'Analyse' : `Optimize (${selectedOrders.size})`}
              </button>
            </div>
            )}
            
            {!activeAnalysis && (
            <div className="organize-view-selector">
              <button
                className="organize-view-toggle"
                onClick={() => setShowViewDropdown(!showViewDropdown)}
              >
                <FaCalendarAlt />
                <span className="organize-view-label">{viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}</span>
              </button>
              
              {showViewDropdown && (
                <div className="organize-view-dropdown">
                  <button
                    className={`organize-view-option ${viewMode === 'month' ? 'active' : ''}`}
                    onClick={() => handleViewChange('month')}
                  >
                    Month
                  </button>
                  <button
                    className={`organize-view-option ${viewMode === 'week' ? 'active' : ''}`}
                    onClick={() => handleViewChange('week')}
                  >
                    Week
                  </button>
                  <button
                    className={`organize-view-option ${viewMode === 'day' ? 'active' : ''}`}
                    onClick={() => handleViewChange('day')}
                  >
                    Day
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Saved Analysis button — hidden when no plans exist or in plan view */}
            {allPlans.length > 0 && !activeAnalysis && (
              <button
                className="organize-saved-analysis-btn"
                onClick={() => {
                  if (allPlans.length > 0) setActiveAnalysis(allPlans[0]);
                }}
                title="View saved plans"
              >
                <FaClipboardList />
                <span className="organize-saved-analysis-label">Plans ({allPlans.length})</span>
              </button>
            )}

            {/* Share + Refresh + Close — right side in plan mode */}
            {activeAnalysis && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '2px' }}>
                <button
                  className="organize-plan-action-btn organize-plan-close-btn"
                  onClick={handleRefreshPlans}
                  disabled={refreshingPlans}
                  title="Refresh plans"
                >
                  <FaRedo className={refreshingPlans ? 'organize-refresh-spinning' : ''} />
                </button>
                <div className="organize-share-container" style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    ref={shareBtnRef}
                    className="organize-plan-action-btn organize-plan-close-btn"
                    onClick={() => setShowShareMenu(v => !v)}
                    title="Share"
                  >
                    <FaShareAlt />
                  </button>
                  {showShareMenu && (() => {
                    const rect = shareBtnRef.current?.getBoundingClientRect();
                    return (
                      <div
                        ref={shareMenuRef}
                        className="organize-share-menu"
                        style={{
                          position: 'fixed',
                          top: rect ? rect.bottom + 4 : 60,
                          left: rect ? rect.left - 80 : 0,
                          right: 'unset',
                        }}
                        onClick={() => setShowShareMenu(false)}
                      >
                        <button
                          className="organize-share-menu-item"
                          onClick={() => { setShowShareMenu(false); handlePrint(); }}
                        >
                          <FaPrint /> Print
                        </button>
                        <button
                          className="organize-share-menu-item"
                          onClick={handleChatShare}
                        >
                          <FaComments /> App Chat
                        </button>
                        <button
                          className="organize-share-menu-item"
                          onClick={() => {
                            const text = buildShareText();
                            if (text) window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                          }}
                        >
                          <FaWhatsapp /> WhatsApp
                        </button>
                        {activeAnalysis?.source !== 'db' && userRole !== 'Mover' && (
                          <button
                            className="organize-share-menu-item"
                            onClick={() => { setShowShareMenu(false); handlePublishPlan(); }}
                            disabled={publishingPlan}
                          >
                            <FaUpload />
                            {publishingPlan ? 'Publishing…' : 'Publish'}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <button
                  className="organize-plan-action-btn organize-plan-close-btn"
                  title="Close plan view"
                  onClick={() => setActiveAnalysis(null)}
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>

          {/* Navigation Bar — shows plan slider when a saved analysis is active, date nav otherwise */}
          {activeAnalysis ? (
            <div className="organize-navigation">
              <div className="organize-nav-center-controls" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Delete — left edge; hidden for Mover */}
                {userRole !== 'Mover' ? (
                  <button
                    className="organize-plan-action-btn organize-plan-delete-btn"
                    title="Delete this plan"
                    onClick={() => { handleDeleteAnalysis(activeAnalysis.id); }}
                    style={{ marginLeft: '4px' }}
                  >
                    <FaTrashAlt />
                  </button>
                ) : (
                  <div style={{ width: '34px', flexShrink: 0 }} />
                )}

                {/* Centered slider */}
                <div className="organize-plan-slider">
                  <button
                    className="organize-nav-btn"
                    title="Previous plan"
                    disabled={allPlans.findIndex(a => a.id === activeAnalysis.id) <= 0}
                    onClick={() => {
                      const idx = allPlans.findIndex(a => a.id === activeAnalysis.id);
                      if (idx > 0) setActiveAnalysis(allPlans[idx - 1]);
                    }}
                  >
                    <FaChevronLeft />
                  </button>

                  <div className={`organize-period-label organize-plan-name${activeAnalysis.source !== 'db' ? ' organize-period-label-local' : ''}`}>
                    {activeAnalysis.name}
                  </div>

                  <button
                    className="organize-nav-btn"
                    title="Next plan"
                    disabled={allPlans.findIndex(a => a.id === activeAnalysis.id) >= allPlans.length - 1}
                    onClick={() => {
                      const idx = allPlans.findIndex(a => a.id === activeAnalysis.id);
                      if (idx < allPlans.length - 1) setActiveAnalysis(allPlans[idx + 1]);
                    }}
                  >
                    <FaChevronRight />
                  </button>
                </div>

                {/* Edit — right edge; hidden for Mover */}
                {userRole !== 'Mover' && (
                <button
                  className="organize-plan-action-btn organize-plan-edit-btn"
                  title="Edit this plan"
                  onClick={() => openEditAnalysisModal(activeAnalysis)}
                  style={{ marginRight: '4px' }}
                >
                  <FaEdit />
                </button>
                )}
              </div>
            </div>
          ) : (
            <div className="organize-navigation">
              <div className="organize-nav-center-controls">
                <button
                  className="organize-nav-btn"
                  onClick={handlePrevious}
                  title={`Previous ${viewMode}`}
                  disabled={isPreviousDisabled()}
                >
                  <FaChevronLeft />
                </button>
                
                <div className="organize-period-label">
                  {formatPeriodLabel()}
                </div>
                
                <button
                  className="organize-nav-btn"
                  onClick={handleNext}
                  title={`Next ${viewMode}`}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {/* Orders Table */}
          <div className="organize-tables-container">
            {activeAnalysis ? (
              /* ── Active saved analysis view ── */
              <div className="organize-active-analysis">
                {/* Two-row summary strip */}
                <div className="organize-active-analysis-meta">
                  {activeAnalysis.summary && (
                    <div className="organize-active-analysis-meta-row">
                      <span className="organize-active-meta-label">Dist:</span>
                      <span className="organize-active-meta-value">{activeAnalysis.summary.totalDistance}</span>
                      <span className="organize-active-analysis-sep">·</span>
                      <span className="organize-active-meta-label">Time:</span>
                      <span className="organize-active-meta-value">{activeAnalysis.summary.totalDuration}</span>
                    </div>
                  )}
                  {(activeAnalysis.startTime || activeAnalysis.endTime) && (
                    <div className="organize-active-analysis-meta-row">
                      {activeAnalysis.startTime && (
                        <>
                          <span className="organize-active-meta-label">Start:</span>
                          <span className="organize-active-meta-value">{activeAnalysis.startTime}</span>
                        </>
                      )}
                      {activeAnalysis.startTime && activeAnalysis.endTime && (
                        <span className="organize-active-analysis-sep">·</span>
                      )}
                      {activeAnalysis.endTime && (
                        <>
                          <span className="organize-active-meta-label">End:</span>
                          <span className="organize-active-meta-value">
                            {activeAnalysis.endTime}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <table className="organize-table organize-analysis-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Requested Time</th>
                      <th>Expected Time</th>
                      <th>Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAnalysis.rows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.name}</td>
                        <td>{row.date}</td>
                        <td>{row.requestedTime || '—'}</td>
                        <td>{row.expectedTime || '—'}</td>
                        <td>{row.distance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : filteredOrders.length > 0 ? (
              <table className="organize-table">
                <thead>
                  <tr>
                    <th className="organize-th-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedOrders.size === filteredOrders.length && filteredOrders.length > 0}
                        onChange={handleSelectAll}
                        title="Select all"
                      />
                    </th>
                    <th className="organize-th-image">Image</th>
                      <th 
                        className="organize-sortable"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleSort('name');
                        }}
                      >
                        Name
                        {sortConfig.key === 'name' && (
                          sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                        {sortConfig.key !== 'name' && <FaSort />}
                      </th>

                      <th 
                        className="organize-sortable"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleSort('distanceValue');
                        }}
                      >
                        Distance
                        {sortConfig.key === 'distanceValue' && (
                          sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                        {sortConfig.key !== 'distanceValue' && <FaSort />}
                      </th>

                      <th 
                        className="organize-sortable"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleSort('deliveryDate');
                        }}
                      >
                        Delivery Date
                        {sortConfig.key === 'deliveryDate' && (
                          sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                        {sortConfig.key !== 'deliveryDate' && <FaSort />}
                      </th>

                      <th 
                        className="organize-sortable"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleSort('deliveryTime');
                        }}
                      >
                        Delivery Time
                        {sortConfig.key === 'deliveryTime' && (
                          sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                        {sortConfig.key !== 'deliveryTime' && <FaSort />}
                      </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, index) => {
                    const orderDate = parseDate(order.deliveryDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isToday = orderDate && 
                      orderDate.getDate() === today.getDate() &&
                      orderDate.getMonth() === today.getMonth() &&
                      orderDate.getFullYear() === today.getFullYear();
                    
                    return (
                      <tr key={`${order.id}-${index}`} className={isToday ? 'organize-today-row' : ''}>
                        <td className="organize-td-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedOrders.has(order.id)}
                            onChange={() => handleCheckboxChange(order.id)}
                          />
                        </td>
                        <td className="organize-td-image">
                          {order.image ? (
                            <img
                              src={order.image}
                              alt="Order"
                              className="organize-order-image"
                              loading="lazy"
                              onClick={() => handleImageClick(order.image)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const nextSibling = e.target.nextElementSibling;
                                if (nextSibling && nextSibling.style) {
                                  nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : (
                            <div className="organize-no-image">
                              No Image
                            </div>
                          )}
                        </td>
                        <td 
                          className="organize-td-name organize-clickable-name"
                          onClick={() => handleCustomerModalClick(order)}
                        >
                          {order.name || 'N/A'}
                        </td>
                        <td className={`organize-td-distance ${order.distance === 'Error' || order.distance === 'Invalid address' || order.distance === 'No address' ? 'organize-distance-error' : ''}`}>
                          {order.distance} {order.duration && order.distance !== 'Error' && order.distance !== 'Invalid address' && order.distance !== 'No address' && ` (${order.duration})`}
                        </td>
                        <td className="organize-td-date">
                          {formatTableDate(order.deliveryDate)}
                        </td>
                        <td className="organize-td-time">
                          {order.deliveryTime || 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="organize-empty">
                No delivery orders found for the selected {viewMode}.
              </div>
            )}
          </div>
        </div>
      </div>

      <ImageZoomModal
        isOpen={zoomModalOpen}
        onRequestClose={() => setZoomModalOpen(false)}
        imageUrl={selectedImageUrl}
      />

      {/* Customer Details Modal */}
      {customerModal && (
        <div 
          className="organize-customer-modal-overlay"
          onClick={(e) => {
            const targetClass = e.target.classList;
            if (targetClass && targetClass.contains('organize-customer-modal-overlay')) {
              closeCustomerModal();
            }
          }}
        >
          <div 
            className="organize-customer-modal-content"
            onClick={e => e.stopPropagation()}
          >
            {/* MODAL HEADER WITH CLOSE BUTTON */}
            <div className="organize-customer-modal-header">
              <h3 className="organize-customer-modal-header-title">{customerModal.name}</h3>
              <button 
                className="organize-customer-modal-close-btn"
                onClick={closeCustomerModal}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* CUSTOMER DETAILS */}
            <div className="organize-customer-modal-details">
              {/* Phone row */}
              <div className="organize-customer-detail-row">
                <span className="organize-customer-detail-label">Phone</span>
                <div className="organize-customer-detail-value">
                  <span className="organize-phone-number-text">{customerModal.phone}</span>
                  {customerModal.phone && customerModal.phone !== 'N/A' && (
                    <FaPhoneAlt 
                      className="organize-phone-action-icon"
                      onClick={() => {
                        const cleanNumber = customerModal.phone.replace(/[^0-9+]/g, '');
                        window.location.href = `tel:${cleanNumber}`;
                      }}
                      title="Call"
                    />
                  )}
                </div>
              </div>
              
              {/* Original Address */}
              <div className="organize-customer-detail-row">
                <span className="organize-customer-detail-label">Original Address</span>
                <div className="organize-customer-detail-value">
                  <span className="organize-address-text">{customerModal.address}</span>
                  {customerModal.address && customerModal.address !== 'N/A' && (
                    <FaMapMarkerAlt 
                      className="organize-map-action-icon"
                      onClick={() => openInGoogleMaps(customerModal.address)}
                      title="Open in Google Maps"
                    />
                  )}
                </div>
              </div>
              
              {/* Corrected/Edited Address - UPDATED with auto-save and delete */}
              <div className="organize-customer-detail-row">
                <span className="organize-customer-detail-label">
                  {customerModal.correctedAddress ? 'Corrected Address' : 'Edit Address'}
                </span>
                <div className="organize-customer-detail-value">
                  {addressEditing ? (
                    <div className="organize-address-edit-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="text"
                        value={editedAddress}
                        onChange={(e) => setEditedAddress(e.target.value)}
                        onBlur={saveAddress}
                        className="organize-address-input"
                        placeholder="Enter corrected address"
                        autoFocus
                        style={{
                          flex: 1,
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '14px'
                        }}
                      />
                      <FaSearchLocation 
                        onClick={suggestCityFromPostalCode}
                        title="Lookup city from postal code"
                        style={{ 
                          cursor: 'pointer',
                          color: '#666',
                          fontSize: '16px'
                        }}
                      />
                      {/* Map Icon at the end for consistency */}
                      <FaMapMarkerAlt
                        className="organize-map-action-icon"
                        onClick={() => openInGoogleMaps(editedAddress)}
                        title="Open in Google Maps"
                        style={{ cursor: 'pointer', color: '#666' }}
                      />
                    </div>
                  ) : (
                    <div className="organize-address-display-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {customerModal.correctedAddress ? (
                        <>
                          <span className="organize-corrected-address-text" style={{ flex: 1 }}>{customerModal.correctedAddress}</span> 
                          {/* Delete button instead of edit */}
                          <FaTrashAlt
                            className="organize-delete-action-icon"
                            onClick={deleteCorrectedAddress}
                            title="Delete corrected address"
                            style={{ cursor: 'pointer', display: 'relative', marginLeft: '5px' }}
                          />
                          {/* Map Icon at the end */}
                          <FaMapMarkerAlt
                            className="organize-map-action-icon"
                            onClick={() => openInGoogleMaps(customerModal.correctedAddress)}
                            title="Open in Google Maps"
                            style={{ cursor: 'pointer', display: 'flex', marginLeft: 'auto' }}
                          />
                        </>
                      ) : (
                        <>
                          <span className="organize-no-corrected-address" style={{ color: '#999', flex: 1 }}>No corrected address</span>
                          {/* Edit icon to start editing */}
                          <FaEdit
                            className="organize-edit-action-icon"
                            onClick={() => {
                              setEditedAddress(customerModal.address || '');
                              setAddressEditing(true);
                            }}
                            title="Edit address"
                            style={{ cursor: 'pointer' }}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Distance and Duration */}
              <div className="organize-customer-detail-row">
                <span className="organize-customer-detail-label">Distance & Time</span>
                <div className="organize-customer-detail-value">
                  <span className="organize-distance-text">
                    {customerModal.distance || 'N/A'}
                    {customerModal.duration && customerModal.distance !== 'Error' && customerModal.distance !== 'Invalid address' && customerModal.distance !== 'No address' && 
                      ` (${customerModal.duration})`}
                  </span>
                </div>
              </div>
              
              {/* Price (Admin/Super only) */}
              {(userRole === 'Admin' || userRole === 'Super') && (
                <div className="organize-customer-detail-row">
                  <span className="organize-customer-detail-label">{`Price (${getCurrencySymbol()})`}</span>
                  <div className="organize-customer-detail-value">
                    <input
                      type="text"
                      value={priceInput}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.,]/g, '');
                        setPriceInput(value);
                      }}
                      onBlur={savePrice}
                      placeholder="Enter price"
                      disabled={priceLoading}
                      className="organize-price-input"
                      style={{
                        width: '80px',
                        padding: '2px 4px',
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        background: 'white',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    {priceLoading && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999' }}>Saving...</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Optimization Route Modal */}
      {optimizeModalOpen && (
        <div 
          className="organize-customer-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeOptimizationModal();
            }
          }}
        >
          <div 
            className="organize-optimize-modal-content"
            onClick={e => {
              e.stopPropagation()
            }}
            style={{ 
              maxWidth: '900px', 
              width: '95%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              background: isDark ? '#2b2b2b' : 'white',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            {/* MODAL HEADER WITH TIME, DATE AND CLOSE BUTTON */}
            <div className="organize-optimization-modal-header" style={{ 
              flexShrink: 0,
              padding: '15px 20px',
              background: isDark ? '#333333' : '#f8f9fa',
              borderBottom: isDark ? '1px solid #3a3a3a' : '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div className="organize-optimization-header-left" style={{ display: 'flex', gap: '20px' }}>
                {optimizationResult && (
                  <>
                    <div className="organize-header-distance">
                      <span className="organize-header-label">Distance:</span>
                      <span className="organize-header-value">{optimizationResult.totalDistance}</span>
                    </div>
                    <div className="organize-header-time">
                      <span className="organize-header-label">Time:</span>
                      <span className="organize-header-value">{optimizationResult.totalDuration}</span>
                    </div>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {optimizationResult && (() => {
                  const _cp = optimizationResult.route.filter(p => p.type === 'customer');
                  return _cp.length > 0;
                })() && (
                  <button
                    className="organize-save-analysis-btn"
                    onClick={openSaveAnalysisModal}
                    title="Save this analysis"
                  >
                    <FaSave />
                  </button>
                )}
                <button 
                  className="organize-optimization-close-btn"
                  onClick={closeOptimizationModal}
                  title="Close"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: isDark ? '#ccc' : '#666',
                    padding: '5px',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Loading State */}
            {optimizationLoading && (
              <div className="organize-optimization-loading" style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '40px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', color: isDark ? '#ccc' : '#666', marginBottom: '10px' }}>
                    Calculating optimal route...
                  </div>
                  <div style={{ fontSize: '14px', color: isDark ? '#999' : '#999' }}>
                    Loading addresses and distances...
                  </div>
                </div>
              </div>
            )}

            {/* Route Table and Map Container - UPDATED LAYOUT */}
            {optimizationResult && (() => {
                const _customerPoints = optimizationResult.route.filter(p => p.type === 'customer');
                const _dates = [...new Set(_customerPoints.map(p => p.order?.deliveryDate).filter(Boolean))];
                const allSameDate = _customerPoints.length > 0 && _dates.length === 1;
                return (
              <div className="organize-optimization-result" style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0, // IMPORTANT: Allows child elements to scroll
              }}>
                {/* Route Table - Scrollable section */}
                <div className="organize-route-table-container" style={{ 
                  flexShrink: 0,
                  maxHeight: '40vh',
                  overflowY: 'auto',
                  borderBottom: '1px solid #eee',
                }}>
                  <table className="organize-route-table" style={{ 
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr className="organize-route-table-header-row" style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <th className="organize-route-th-order" style={{ 
                          padding: '12px 8px',
                          textAlign: 'center',
                          width: '50px',
                          fontWeight: '600',
                          borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6'
                        }}></th>
                        <th className="organize-route-th-from" style={{ 
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6'
                        }}>From</th>
                        <th className="organize-route-th-to" style={{ 
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6'
                        }}>To</th>
                        {allSameDate && (
                          <th className="organize-route-th-del-time">Del. Time</th>
                        )}
                        <th className="organize-route-th-distance" style={{ 
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6'
                        }}>Distance</th>
                        <th className="organize-route-th-time" style={{ 
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6'
                        }}>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optimizationResult.route.map((point, index) => {
                        if (index === optimizationResult.route.length - 1) return null;
                        
                        const isAdmin = point.type === 'admin';
                        const nextPoint = optimizationResult.route[index + 1];
                        const isLastCustomer = nextPoint && nextPoint.type === 'admin' && nextPoint.name === 'Admin (End)';
                        
                        // Calculate letter for the current point
                        const getCurrentLetter = (idx) => {
                          // Skip Admin (End) which is the last element
                          const filteredRoute = optimizationResult.route.filter(p => p.name !== 'Admin (End)');
                          const currentIndex = filteredRoute.findIndex(p => p.id === point.id);
                          
                          if (currentIndex === 0) return 'A'; // Home
                          return String.fromCharCode(65 + currentIndex); // B, C, D, etc.
                        };
                        
                        // Calculate letter for the next point
                        const getNextLetter = () => {
                          if (isLastCustomer) return '(A)';
                          
                          const filteredRoute = optimizationResult.route.filter(p => p.name !== 'Admin (End)');
                          const nextPointInFiltered = filteredRoute.find(p => p.id === nextPoint.id);
                          const nextIndex = filteredRoute.findIndex(p => p.id === nextPoint.id);
                          
                          if (nextIndex === 0) return '(A)'; // Home
                          return `(${String.fromCharCode(65 + nextIndex)})`; // (B), (C), etc.
                        };
                        
                        const currentLetter = getCurrentLetter(index);
                        const nextLetter = getNextLetter();
                        
                        return (
                          <React.Fragment key={`${point.id}-${index}`}>
                            <tr 
                              className={`organize-route-row ${point.type === 'admin' ? 'organize-route-admin' : 'organize-route-customer'}`}
                              style={{ 
                                borderBottom: isDark ? '1px solid #3a3a3a' : '1px solid #eee',
                                backgroundColor: isDark ? '#2b2b2b' : 'white',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              <td className="organize-route-td-order" style={{ 
                                padding: '12px 8px',
                                textAlign: 'center',
                                verticalAlign: 'middle'
                              }}>
                                {isAdmin ? (
                                  <div className="organize-route-admin-icon" style={{ 
                                    fontSize: '18px',
                                    lineHeight: '1',
                                    display: 'flex',
                                    gap: '2px',
                                    justifyContent: 'center'
                                  }}><FaTruck /></div>
                                ) : (
                                  <button 
                                    className="organize-route-arrow-btn"
                                    onClick={() => handleMoveUp(point.id)}
                                    disabled={index <= 1}
                                    title="Move up"
                                  >
                                    <FaArrowUp />
                                  </button>
                                )}
                              </td>
                              <td className="organize-route-td-from" style={{ 
                                padding: '12px 8px',
                                verticalAlign: 'middle',
                                fontWeight: isAdmin ? '600' : '400'
                              }}>
                                {/* UPDATED: Show letter for all points */}
                                {point.name === 'Home' ? `Home (${currentLetter})` : `${point.name} (${currentLetter})`}
                                {point.type === 'dummy' && (
                                  <span className="organize-dummy-tag" style={{
                                    fontSize: '12px',
                                    color: '#dc3545',
                                    marginLeft: '5px',
                                    background: isDark ? '#3a2020' : '#ffe6e6',
                                    padding: '1px 5px',
                                    borderRadius: '3px'
                                  }}></span>
                                )}
                              </td>
                              <td className="organize-route-td-to" style={{ 
                                padding: '12px 8px',
                                verticalAlign: 'middle',
                                fontWeight: isLastCustomer ? '600' : '400'
                              }}>
                                {/* UPDATED: Show letter for next points except last Home */}
                                {isLastCustomer ? 'Home' : nextPoint ? `${nextPoint.name} ${nextLetter}` : 'N/A'}
                              </td>
                              {allSameDate && (
                                <td className="organize-route-td-del-time">
                                  {nextPoint?.type === 'customer' ? (nextPoint.order?.deliveryTime || '-') : ''}
                                </td>
                              )}
                              <td className="organize-route-td-distance">
                                {point.toNext ? point.toNext.distance : '-'}
                              </td>
                              <td className="organize-route-td-time" style={{ verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                                <span style={{ flex: '1 1 auto' }}>
                                  {point.toNext ? point.toNext.duration : '-'}
                                </span>
                                {point.type === 'dummy' && (
                                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '2px', flexShrink: 0 }}>
                                    {/* Edit button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newRoute = optimizationRoute.map(p =>
                                          p.id === point.id ? { ...p, isEditing: true } : p
                                        );
                                        setOptimizationRoute(newRoute);
                                      }}
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: isDark ? '#ccc' : '#666',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        borderRadius: '3px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      title="Edit test customer"
                                    >
                                      <FaEdit />
                                    </button>
                                    {/* Delete button */}
                                    <button
                                      onClick={async (e) => {
                                      e.stopPropagation();
                                      
                                      try {
                                        // Remove the dummy record
                                        const newRoute = optimizationRoute.filter(p => p.id !== point.id);
                                        
                                        // Update state immediately
                                        setOptimizationRoute(newRoute);
                                        
                                        // Create a fresh route array for recalculation
                                        const routeForRecalculation = newRoute.map(item => ({ ...item }));
                                        
                                        // Clear existing toNext distances since they're now invalid
                                        routeForRecalculation.forEach(item => {
                                          if (item.toNext) {
                                            item.toNext = null;
                                          }
                                        });
                                        
                                        // Update optimization result with cleared distances
                                        const updatedResult = {
                                          ...optimizationResult,
                                          route: routeForRecalculation,
                                          totalDistance: 'Recalculating...',
                                          totalDuration: 'Recalculating...'
                                        };
                                        setOptimizationResult(updatedResult);
                                        
                                        // Recalculate all distances
                                        await recalculateDistances(routeForRecalculation);
                                        
                                      } catch (error) {
                                        console.error('Error deleting dummy record:', error);
                                        // Fallback: just remove the record without recalculating
                                        const newRoute = optimizationRoute.filter(p => p.id !== point.id);
                                        setOptimizationRoute(newRoute);
                                        
                                        if (optimizationResult) {
                                          const updatedResult = {
                                            ...optimizationResult,
                                            route: newRoute
                                          };
                                          setOptimizationResult(updatedResult);
                                        }
                                      }
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: isDark ? '#ccc' : '#666',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      borderRadius: '3px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title="Delete test customer"
                                    >
                                      <FaTrashAlt />
                                    </button>
                                  </div>
                                )}
                                </div>
                              </td>
                            </tr>
                            
                            {index < optimizationRoute.length - 2 && (
                              <tr className="organize-route-connector">
                                <td colSpan={allSameDate ? 6 : 5} style={{ padding: '0' }}>
                                  <div className="organize-route-connector-line" style={{
                                    height: '1px',
                                    margin: '0 60px'
                                  }}></div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="organize-add-dummy-container" style={{
                    marginTop: '10px',
                    padding: '0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}>
                    <button 
                      className="organize-add-dummy-btn"
                      onClick={() => {
                        const newDummy = {
                          id: `dummy-${Date.now()}`,
                          type: 'dummy',
                          name: 'Test Customer',
                          address: '',
                          isEditing: true,
                          addressParts: {
                            pincode: '',
                            housenumber: '',
                            street: ''
                          },
                          order: null,
                          toNext: null
                        };
                        
                        // Insert dummy before the Admin (End) which is the last element
                        const newRoute = [...optimizationRoute];
                        const adminEndIndex = newRoute.length - 1;
                        newRoute.splice(adminEndIndex, 0, newDummy);
                        
                        setOptimizationRoute(newRoute);
                        
                        // Update optimization result
                        if (optimizationResult) {
                          setOptimizationResult({
                            ...optimizationResult,
                            route: newRoute
                          });
                        }
                      }}
                      style={{
                        background: 'transparent',
                        color: isDark ? '#ccc' : '#333',
                        border: 'none',
                        borderRadius: '4px',
                        width: '30px',
                        height: '30px',
                        fontSize: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Google Map Section - UPDATED WITH TOUCH SUPPORT */}
                <div className="organize-optimization-map-container" style={{ 
                  flex: 1,
                  minHeight: '40vh',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}>                  
                  {/* Map Container with touch support */}
                  <div 
                    ref={mapContainerRef}
                    style={{ 
                      flex: 1,
                      minHeight: '300px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      border: isDark ? '1px solid #3a3a3a' : '1px solid #ddd',
                      background: isDark ? '#2b2b2b' : '#f8f9fa',
                      position: 'relative',
                      touchAction: 'pan-x pan-y pinch-zoom', // Enable touch interactions
                    }}
                  >
                    {/* Loading overlay */}
                    {(!window.google || !window.google.maps || mapLoading) && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isDark ? '#2b2b2b' : '#f8f9fa',
                        zIndex: 1
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          border: isDark ? '3px solid #333' : '3px solid #f3f3f3',
                          borderTop: '3px solid #fa4848',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <div style={{ 
                          marginTop: '10px', 
                          fontSize: '14px', 
                          color: isDark ? '#999' : '#666' 
                        }}>
                          {!window.google || !window.google.maps ? 'Loading Google Maps...' : 'Loading map...'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>       
              </div>
            );
            })()}
          </div>
          
          {/* Dummy Customer Address Input Modal (keep this part exactly as before) */}
          {optimizationRoute.some(point => point.type === 'dummy' && point.isEditing) && (
            <div className="organize-customer-modal-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1002
            }}>
              <div 
                className="organize-dummy-address-modal"
                onClick={e => e.stopPropagation()}
                style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  width: '400px',
                  maxWidth: '90vw',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.3)'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>Test Address</h3>
                  <button 
                    onClick={() => {
                      const editingDummy = optimizationRoute.find(point => 
                        point.type === 'dummy' && point.isEditing
                      );
                      
                      if (editingDummy) {
                        // If already saved (has a street), just close the modal
                        if (editingDummy.addressParts?.street || editingDummy.name) {
                          const newRoute = optimizationRoute.map(p =>
                            p.id === editingDummy.id ? { ...p, isEditing: false } : p
                          );
                          setOptimizationRoute(newRoute);
                        } else {
                          // Brand new unsaved record � remove it
                          const newRoute = optimizationRoute.filter(point => point.id !== editingDummy.id);
                          setOptimizationRoute(newRoute);
                          if (optimizationResult) {
                            setOptimizationResult({ ...optimizationResult, route: newRoute });
                          }
                        }
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >&#x2715;</button>
                </div>
                
                {optimizationRoute.map((point, index) => {
                  if (point.type === 'dummy' && point.isEditing) {
                    return (
                      <div key={point.id}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr',
                          gap: '10px',
                          marginBottom: '15px'
                        }}>
                          <div style={{ position: 'relative' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                              Pincode / Location
                            </label>
                            <input
                              type="text"
                              value={point.addressParts?.pincode || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newRoute = [...optimizationRoute];
                                const dummyIndex = newRoute.findIndex(p => p.id === point.id);
                                newRoute[dummyIndex] = {
                                  ...newRoute[dummyIndex],
                                  addressParts: {
                                    ...newRoute[dummyIndex].addressParts,
                                    pincode: val,
                                    // Clear stored coords when field changes
                                    lat: undefined,
                                    lon: undefined
                                  }
                                };
                                setOptimizationRoute(newRoute);

                                if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
                                if (val.length >= 2) {
                                  setLocationLoading(true);
                                  locationDebounceRef.current = setTimeout(async () => {
                                    try {
                                      // Load static German PLZ dataset once and cache it
                                      if (!allLocalitiesRef.current && !localitiesLoadingRef.current) {
                                        localitiesLoadingRef.current = true;
                                        const res = await fetch('/de-plz.json');
                                        // Format: [[postcode, cityName], ...]
                                        allLocalitiesRef.current = await res.json();
                                        localitiesLoadingRef.current = false;
                                      }
                                      // Wait if another request is already loading
                                      let attempts = 0;
                                      while (localitiesLoadingRef.current && attempts++ < 40) {
                                        await new Promise(r => setTimeout(r, 100));
                                      }
                                      const all = allLocalitiesRef.current || [];
                                      const isNumeric = /^\d+$/.test(val.trim());
                                      const valLower = val.toLowerCase();
                                      let suggestions;
                                      if (isNumeric) {
                                        // Postcode prefix match: data is sorted, find matching entries
                                        suggestions = all
                                          .filter(r => r[0].startsWith(val))
                                          .slice(0, 8)
                                          .map(r => ({ postcode: r[0], city: r[1], isNumeric: true }));
                                      } else {
                                        // City name prefix: deduplicate by city name
                                        const seen = new Set();
                                        suggestions = [];
                                        for (const r of all) {
                                          const name = r[1];
                                          if (name.toLowerCase().startsWith(valLower) && !seen.has(name)) {
                                            seen.add(name);
                                            suggestions.push({ postcode: r[0], city: name, isNumeric: false });
                                            if (suggestions.length === 8) break;
                                          }
                                        }
                                        suggestions.sort((a, b) => a.city.localeCompare(b.city, 'de'));
                                      }
                                      setLocationSuggestions(suggestions);
                                    } catch {
                                      setLocationSuggestions([]);
                                    } finally {
                                      setLocationLoading(false);
                                    }
                                  }, 200);
                                } else {
                                  setLocationSuggestions([]);
                                  setLocationLoading(false);
                                }
                              }}
                              onBlur={() => setTimeout(() => setLocationSuggestions([]), 200)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: locationSuggestions.length > 0 || locationLoading ? '4px 4px 0 0' : '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                              }}
                            />
                            {(locationLoading || locationSuggestions.length > 0) && (
                              <div className="autocomplete-dropdown" style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                border: '1px solid #ddd',
                                borderTop: 'none',
                                borderRadius: '0 0 4px 4px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 30,
                                maxHeight: '200px',
                                overflowY: 'auto'
                              }}>
                                {locationLoading && (
                                  <div style={{ padding: '8px 12px', fontSize: '13px', color: '#999' }}>Searching...</div>
                                )}
                                {locationSuggestions.map((s, i) => (
                                  <div
                                    key={i}
                                    onMouseDown={(ev) => {
                                      ev.preventDefault();
                                      const label = s.isNumeric
                                        ? `${s.postcode} ${s.city}`.trim()
                                        : s.city;
                                      const newRoute = [...optimizationRoute];
                                      const dummyIndex = newRoute.findIndex(p => p.id === point.id);
                                      newRoute[dummyIndex] = {
                                        ...newRoute[dummyIndex],
                                        addressParts: {
                                          ...newRoute[dummyIndex].addressParts,
                                          pincode: label,
                                          pincodeRaw: s.isNumeric ? s.postcode : '',
                                          cityRaw: s.city
                                        }
                                      };
                                      setOptimizationRoute(newRoute);
                                      setLocationSuggestions([]);
                                    }}
                                    className="autocomplete-item"
                                    style={{ padding: '8px 12px', fontSize: '14px', cursor: 'pointer', borderBottom: i < locationSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                                  >
                                    {s.isNumeric ? (
                                      <>
                                        <span style={{ fontWeight: '600' }}>{s.postcode}</span>
                                        <span style={{ margin: '0 6px', color: '#aaa' }}>·</span>
                                        <span style={{ color: '#555' }}>{s.city}</span>
                                      </>
                                    ) : (
                                      <span>{s.city}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                              House Number
                            </label>
                            <input
                              type="text"
                              value={point.addressParts?.housenumber || ''}
                              onChange={(e) => {
                                const newRoute = [...optimizationRoute];
                                const dummyIndex = newRoute.findIndex(p => p.id === point.id);
                                newRoute[dummyIndex] = {
                                  ...newRoute[dummyIndex],
                                  addressParts: {
                                    ...newRoute[dummyIndex].addressParts,
                                    housenumber: e.target.value
                                  }
                                };
                                setOptimizationRoute(newRoute);
                              }}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            />
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '20px', position: 'relative' }}>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                            Street Name
                          </label>
                          <input
                            type="text"
                            value={point.addressParts?.street || ''}
                            onChange={async (e) => {
                              const val = e.target.value;
                              const newRoute = [...optimizationRoute];
                              const dummyIndex = newRoute.findIndex(p => p.id === point.id);
                              newRoute[dummyIndex] = {
                                ...newRoute[dummyIndex],
                                addressParts: { ...newRoute[dummyIndex].addressParts, street: val }
                              };
                              setOptimizationRoute(newRoute);

                              if (streetDebounceRef.current) clearTimeout(streetDebounceRef.current);
                              if (val.length >= 3) {
                                setStreetLoading(true);
                                streetDebounceRef.current = setTimeout(async () => {
                                  try {
                                    // Read location from the CURRENT point in live state (not stale closure)
                                    const liveParts = optimizationRoute.find(p => p.id === point.id)?.addressParts || {};
                                    // Prefer dropdown-set raw values; fall back to whatever the pincode field has
                                    const rawPincode = liveParts.pincodeRaw || '';
                                    const rawCity   = liveParts.cityRaw   || '';
                                    const pincodeField = (liveParts.pincode || '').trim();
                                    // Detect if pincode field has a plain 5-digit number (manual entry)
                                    const pincodeNumeric = rawPincode || (/^\d{5}$/.test(pincodeField) ? pincodeField : '');
                                    // City: prefer dropdown cityRaw, then non-numeric pincode field value
                                    const cityName = rawCity || (rawPincode ? '' : (/^\d/.test(pincodeField) ? '' : pincodeField.split(' ').slice(-1)[0]));
                                    const valLower = val.toLowerCase();
                                    let rows = [];

                                    if (pincodeNumeric) {
                                      // Strategy 1: fetch ALL streets for that postcode once and cache
                                      if (!streetCacheRef.current[pincodeNumeric]) {
                                        const pages = [];
                                        for (let pg = 1; pg <= 10; pg++) {
                                          const d = await fetch(
                                            `https://openplzapi.org/de/Streets?postalCode=${encodeURIComponent(pincodeNumeric)}&pageSize=50&page=${pg}`
                                          ).then(r => r.json()).catch(() => []);
                                          if (!Array.isArray(d) || d.length === 0) break;
                                          pages.push(...d);
                                          if (d.length < 50) break;
                                        }
                                        streetCacheRef.current[pincodeNumeric] = pages;
                                      }
                                      rows = streetCacheRef.current[pincodeNumeric];
                                    } else if (cityName) {
                                      // Strategy 2: Streets?name=prefix&locality=city  (API supports regex/substring)
                                      const d = await fetch(
                                        `https://openplzapi.org/de/Streets?name=${encodeURIComponent(val)}&locality=${encodeURIComponent(cityName)}&pageSize=50`
                                      ).then(r => r.json()).catch(() => []);
                                      rows = Array.isArray(d) ? d : [];
                                      // Fallback: no wildcard (API sometimes ignores it)
                                      if (rows.filter(r => (r.name || '').toLowerCase().startsWith(valLower)).length === 0) {
                                        const d2 = await fetch(
                                          `https://openplzapi.org/de/Streets?locality=${encodeURIComponent(cityName)}&pageSize=50`
                                        ).then(r => r.json()).catch(() => []);
                                        if (Array.isArray(d2) && d2.length > 0) rows = d2;
                                      }
                                    } else {
                                      // Strategy 3: name prefix only, return up to 50 results
                                      const d = await fetch(
                                        `https://openplzapi.org/de/Streets?name=${encodeURIComponent(val)}&pageSize=50`
                                      ).then(r => r.json()).catch(() => []);
                                      rows = Array.isArray(d) ? d : [];
                                    }

                                    // Client-side prefix filter
                                    const filtered = rows.filter(r => (r.name || '').toLowerCase().startsWith(valLower));

                                    // Count occurrences per name to know when postcode is needed
                                    const nameCount = {};
                                    filtered.forEach(r => { nameCount[r.name] = (nameCount[r.name] || 0) + 1; });

                                    // Deduplicate by name+postcode, cap at 10
                                    const seen = new Set();
                                    const suggestions = [];
                                    for (const r of filtered) {
                                      const key = `${r.name}|${r.postalCode}`;
                                      if (seen.has(key)) continue;
                                      seen.add(key);
                                      suggestions.push({
                                        name: r.name,
                                        postcode: nameCount[r.name] > 1 ? (r.postalCode || '') : '',
                                        locality: nameCount[r.name] > 1 ? (r.locality || '') : ''
                                      });
                                      if (suggestions.length >= 10) break;
                                    }
                                    suggestions.sort((a, b) => a.name.localeCompare(b.name, 'de'));
                                    setStreetSuggestions(suggestions);
                                  } catch {
                                    setStreetSuggestions([]);
                                  } finally {
                                    setStreetLoading(false);
                                  }
                                }, 400);
                              } else {
                                setStreetSuggestions([]);
                                setStreetLoading(false);
                              }
                            }}
                            onBlur={() => setTimeout(() => setStreetSuggestions([]), 200)}
                            placeholder="e.g., Hauptstraße"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: streetSuggestions.length > 0 || streetLoading ? '4px 4px 0 0' : '4px',
                              fontSize: '14px',
                              boxSizing: 'border-box'
                            }}
                          />
                          {(streetLoading || streetSuggestions.length > 0) && (
                            <div className="autocomplete-dropdown" style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              border: '1px solid #ddd',
                              borderTop: 'none',
                              borderRadius: '0 0 4px 4px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              zIndex: 20,
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {streetLoading && (
                                <div style={{ padding: '8px 12px', fontSize: '13px', color: '#999' }}>Searching...</div>
                              )}
                              {streetSuggestions.map((s, i) => (
                                <div
                                  key={i}
                                  onMouseDown={(ev) => {
                                    ev.preventDefault();
                                    const newRoute = [...optimizationRoute];
                                    const dummyIndex = newRoute.findIndex(p => p.id === point.id);
                                    newRoute[dummyIndex] = {
                                      ...newRoute[dummyIndex],
                                      addressParts: { ...newRoute[dummyIndex].addressParts, street: s.name }
                                    };
                                    setOptimizationRoute(newRoute);
                                    setStreetSuggestions([]);
                                  }}
                                  className="autocomplete-item"
                                  style={{ padding: '8px 12px', fontSize: '14px', cursor: 'pointer', borderBottom: i < streetSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                  <span>{s.name}</span>
                                  {s.postcode && (
                                    <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                                      {s.postcode}{s.locality ? ` ${s.locality}` : ''}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'flex-end',
                          gap: '10px'
                        }}>
                          <button
                            onClick={() => {
                              // Remove the dummy record
                              const newRoute = optimizationRoute.filter(p => p.id !== point.id);
                              setOptimizationRoute(newRoute);
                              
                              if (optimizationResult) {
                                setOptimizationResult({
                                  ...optimizationResult,
                                  route: newRoute
                                });
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#f5f5f5',
                              color: '#333',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            Cancel
                          </button>
                          
                          <button
                            onClick={async () => {
                              // Validate: at minimum pincode/location is required
                              if (!point.addressParts?.pincode) {
                                showAlert('Please fill in the Pincode / Location field');
                                return;
                              }

                              // Build address string � street and house number both optional when pincode given
                              const pc  = point.addressParts.pincodeRaw || point.addressParts.pincode;
                              const st  = point.addressParts.street?.trim();
                              const hn  = point.addressParts.housenumber?.trim();
                              const address = st
                                ? (hn ? `${st} ${hn}, ${pc}, Germany` : `${st}, ${pc}, Germany`)
                                : `${pc}, Germany`;
                              
                              // Update the dummy record
                              const newRoute = [...optimizationRoute];
                              const dummyIndex = newRoute.findIndex(p => p.id === point.id);
                              
                              newRoute[dummyIndex] = {
                                ...newRoute[dummyIndex],
                                address: address,
                                isEditing: false
                                // Keep addressParts so the record can be re-edited later
                              };
                              
                              setOptimizationRoute(newRoute);
                              
                              // Recalculate distances including the new dummy record
                              await recalculateDistances(newRoute);
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#fa4848',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            Add to Route
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Save Analysis Modal ─────────────────────────────────────────── */}
      {showSaveAnalysisModal && (
        <div
          className="organize-modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1010, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowSaveAnalysisModal(false); setEditingAnalysisId(null); } }}
        >
          <div
            className="organize-save-analysis-modal"
            onClick={e => e.stopPropagation()}
            style={{
              background: isDark ? '#2b2b2b' : 'white',
              borderRadius: '8px',
              width: '95%',
              maxWidth: '700px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 5px 25px rgba(0,0,0,0.35)'
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px',
              borderBottom: isDark ? '1px solid #3a3a3a' : '1px solid #dee2e6',
              background: isDark ? '#333' : '#f8f9fa',
              flexShrink: 0
            }}>
              <span style={{ fontWeight: 600, fontSize: '16px' }}>{editingAnalysisId ? 'Edit Delivery Plan' : 'Save Delivery Plan'}</span>
              <button
                onClick={() => { setShowSaveAnalysisModal(false); setEditingAnalysisId(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: isDark ? '#ccc' : '#666' }}
              ><FaTimes /></button>
            </div>

            {/* Route table */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: isDark ? '#333' : '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6' }}>Name</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6' }}>Req. Time</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6' }}>Expected Time</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: isDark ? '2px solid #3a3a3a' : '2px solid #dee2e6' }}>Distance</th>
                  </tr>
                </thead>
                <tbody>
                  {saveAnalysisRows.map((row, idx) => (
                    <tr key={row.id} style={{ borderBottom: isDark ? '1px solid #3a3a3a' : '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px' }}>{row.name}</td>
                      <td style={{ padding: '10px 12px' }}>{row.requestedTime || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <input
                          type="time"
                          value={row.expectedTime}
                          onChange={e => {
                            const newTime = e.target.value;
                            if (!newTime) {
                              setSaveAnalysisRows(prev => prev.map((r, i) => i === idx ? { ...r, expectedTime: '' } : r));
                              return;
                            }
                            // Back-calculate start time from this row's manually entered time
                            let cumMins = 0;
                            for (let i = 0; i <= idx; i++) cumMins += saveAnalysisRows[i].incomingDurationMins;
                            const newStartTime = addMinsToTime(newTime, -cumMins);
                            // Forward-propagate from the derived start time across all rows
                            const recalculated = recalcExpectedTimes(saveAnalysisRows, newStartTime);
                            // Pin the edited row to exactly what the user typed
                            recalculated[idx] = { ...recalculated[idx], expectedTime: newTime };
                            setSaveAnalysisRows(recalculated);
                            setSaveAnalysisStartTime(newStartTime);
                            const dateStr = saveAnalysisRows[0]?.date || '';
                            setSaveAnalysisName(generateAnalysisName(dateStr, newStartTime));
                          }}
                          style={{
                            border: isDark ? '1px solid #3a3a3a' : '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '4px 6px',
                            fontSize: '13px',
                            background: isDark ? '#2b2b2b' : 'white',
                            color: isDark ? '#eee' : '#333',
                            width: '100px'
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px' }}>{row.incomingDistance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Start time + name + buttons */}
            <div style={{
              padding: '14px 18px',
              borderTop: isDark ? '1px solid #3a3a3a' : '1px solid #dee2e6',
              background: isDark ? '#2b2b2b' : 'white',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', overflow: 'hidden' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>Start Time:</label>
                <input
                  type="time"
                  value={saveAnalysisStartTime}
                  onChange={e => {
                    const val = e.target.value;
                    setSaveAnalysisStartTime(val);
                    const recalculated = recalcExpectedTimes(saveAnalysisRows, val);
                    setSaveAnalysisRows(recalculated);
                    // Only auto-update plan name for new plans, not edits
                    if (!editingAnalysisId) {
                      const dateStr = saveAnalysisRows[0]?.date || '';
                      setSaveAnalysisName(generateAnalysisName(dateStr, val));
                    }
                  }}
                  style={{
                    border: isDark ? '1px solid #3a3a3a' : '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '6px 6px',
                    fontSize: '14px',
                    background: isDark ? '#2b2b2b' : 'white',
                    color: isDark ? '#eee' : '#333',
                    width: '110px'
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: isDark ? '#ccc' : '#555' }}>End Time:</span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: isDark ? '#eee' : '#333',
                  minWidth: '50px'
                }}>
                  {(() => {
                    const lastRow = saveAnalysisRows[saveAnalysisRows.length - 1];
                    if (!lastRow?.expectedTime) return '';
                    const t = addMinsToTime(lastRow.expectedTime, saveAnalysisReturnMins);
                    return t + (saveAnalysisReturnDistance ? ` (↩ ${saveAnalysisReturnDistance})` : '');
                  })()}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>Plan Name:</label>
                <input
                  type="text"
                  value={saveAnalysisName}
                  onChange={e => setSaveAnalysisName(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: '180px',
                    border: isDark ? '1px solid #3a3a3a' : '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '6px 10px',
                    fontSize: '14px',
                    background: isDark ? '#2b2b2b' : 'white',
                    color: isDark ? '#eee' : '#333'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => { setShowSaveAnalysisModal(false); setEditingAnalysisId(null); }}
                  style={{
                    padding: '8px 18px', background: isDark ? '#3a3a3a' : '#f5f5f5',
                    color: isDark ? '#ccc' : '#333', border: isDark ? '1px solid #4a4a4a' : '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAnalysis}
                  style={{
                    padding: '8px 18px', background: isDark ? '#3a3a3a' : '#f5f5f5',
                    color: isDark ? '#ccc' : '#333', border: isDark ? '1px solid #4a4a4a' : '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <FaSave /> {editingAnalysisId ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Saved Analysis List Modal ──────────────────────────────────── */}
    </div>
  );
};

export default Organize;
